/**
 * ERP RM Desarrollos — Diccionario Unificado de Tipos
 * Fuente de verdad: 02_DDL/DDL_v14_MASTER.sql (PARTS 01–11)
 * Auto-generado 2026-05-01 — NO modificar a mano; re-generar desde DDL.
 * Corregido vs BD real 2026-05-01 (FASE-1 validación).
 *
 * Importable desde backend: import { ERP } from '../../packages/shared/types/erp-dictionary'
 * Importable desde frontend: import { ERP } from '../../../packages/shared/types/erp-dictionary'
 */

// ─────────────────────────────────────────────────────────────────────────────
// M00 — SISTEMA
// ─────────────────────────────────────────────────────────────────────────────

export const USUARIO_ROL = {
  VENDEDOR: 'vendedor',
  AUXILIAR_COMERCIAL: 'auxiliar_comercial',
  AUXILIAR_ADMINISTRATIVO: 'auxiliar_administrativo',
  AUXILIAR_COMPRAS: 'auxiliar_compras',
  ARQUITECTO: 'arquitecto',
  GERENTE_VENTAS: 'gerente_ventas',
  GERENTE_POSTVENTA: 'gerente_postventa',
  CONTABILIDAD_CXP: 'contabilidad_cxp',
  // D-2026-10-02-A/H (Mau 2-oct 10:25 y 10:53, «2 IGUAL QUE EL CONTADOR») — mismo acceso que contabilidad_cxp
  ADMINISTRADOR: 'administrador',
  COBRANZA: 'cobranza',
  LEGAL: 'legal',
  GERENTE_PROYECTO: 'gerente_proyecto',
  CFO: 'cfo',
  CEO: 'ceo',
} as const;
export type UsuarioRol = (typeof USUARIO_ROL)[keyof typeof USUARIO_ROL];

export const USUARIO_NIVEL = {
  N1: 1,
  N2: 2,
  N3: 3,
  N4: 4,
} as const;
export type UsuarioNivel = (typeof USUARIO_NIVEL)[keyof typeof USUARIO_NIVEL];

/** Roles agrupados por nivel — útil para verificarNivel() */
export const ROLES_POR_NIVEL: Record<number, UsuarioRol[]> = {
  1: ['vendedor', 'auxiliar_comercial', 'auxiliar_administrativo', 'auxiliar_compras'],
  2: ['arquitecto', 'gerente_ventas', 'gerente_postventa', 'contabilidad_cxp', 'administrador', 'cobranza', 'legal'],
  3: ['gerente_proyecto', 'cfo'],
  4: ['ceo'],
};

/** Roles que ven el RITMO de venta (`GET /proyectos/:id/ritmo-venta`, `verificarRol`). Una sola lista: la usa la ruta y la casa para no pedir lo
 *  que el rol no ve (BUG-4064). Antes vivía suelta en `proyectos.routes.ts`. */
export const ROLES_VENTAS: readonly UsuarioRol[] = ['gerente_ventas', 'gerente_proyecto', 'cfo', 'ceo'];

/** proyectos.estado — enum('activo','pausado','entregado','cancelado') */
export const PROYECTO_ESTADO = {
  ACTIVO: 'activo',
  PAUSADO: 'pausado',
  ENTREGADO: 'entregado',
  CANCELADO: 'cancelado',
} as const;
export type ProyectoEstado = (typeof PROYECTO_ESTADO)[keyof typeof PROYECTO_ESTADO];

/** Etapas de proyecto — concepto de spec, sin columna ENUM en BD */
export const PROYECTO_ETAPA = {
  PREVENTA: 'preventa',
  CONSTRUCCION: 'construccion',
  ENTREGA: 'entrega',
  POSTVENTA: 'postventa',
} as const;
export type ProyectoEtapa = (typeof PROYECTO_ETAPA)[keyof typeof PROYECTO_ETAPA];

/** socios.tipo_participacion — enum('persona_fisica','persona_moral') */
export const SOCIO_TIPO_PARTICIPACION = {
  PERSONA_FISICA: 'persona_fisica',
  PERSONA_MORAL: 'persona_moral',
} as const;
export type SocioTipoParticipacion = (typeof SOCIO_TIPO_PARTICIPACION)[keyof typeof SOCIO_TIPO_PARTICIPACION];

/** Rol de socio — concepto de negocio, sin columna ENUM propia en BD */
export const SOCIO_ROL = {
  INVERSIONISTA: 'inversionista',
  CONSTRUCTOR: 'constructor',
  PROVEEDOR: 'proveedor',
  BROKER: 'broker',
  OTRO: 'otro',
} as const;
export type SocioRol = (typeof SOCIO_ROL)[keyof typeof SOCIO_ROL];

/** moneda — enum('MXN','USD') — campo multi-módulo: ingresos_registros, egresos, creditos, etc. */
export const MONEDA = {
  MXN: 'MXN',
  USD: 'USD',
} as const;
export type Moneda = (typeof MONEDA)[keyof typeof MONEDA];

// ─────────────────────────────────────────────────────────────────────────────
// M01 — INGRESOS
// ─────────────────────────────────────────────────────────────────────────────

/** ingresos_registros.tipo_ingreso — enum('venta_unidad','anticipo_enganche','apartado_garantia','credito','aportacion_socio','otros_ingresos') */
export const INGRESO_TIPO = {
  VENTA_UNIDAD: 'venta_unidad',
  ANTICIPO_ENGANCHE: 'anticipo_enganche',
  APARTADO_GARANTIA: 'apartado_garantia',
  CREDITO: 'credito',
  APORTACION_SOCIO: 'aportacion_socio',
  OTROS_INGRESOS: 'otros_ingresos',
} as const;
export type IngresoTipo = (typeof INGRESO_TIPO)[keyof typeof INGRESO_TIPO];

/**
 * BUG-3968 — DOS preguntas distintas que hoy coinciden en los mismos dos tipos, y por eso llevan DOS listas (si mañana Mau agrega un tipo de
 * venta que no es cuota —una venta de contado, por ejemplo— sólo una de las dos debe cambiar; unificarlas sería el BUG-1744):
 *
 *  · `TIPOS_DE_COBRO_DE_CUOTA` — «¿este cobro es una CUOTA del plan de pagos (o su captura manual, que compite con ella)?»: se registra con «Registrar
 *    cobro», queda PAGADO de una vez y no pasa por «autorizar». Lo usa el candado de cuota pendiente (`ingresos.service.ts`, `assertSinCuotaPendiente`): ahí
 *    la captura aún NO trae contrato, así que sólo se puede decidir por tipo. La segunda firma, en cambio, ya no mira el tipo: decide por ORIGEN
 *    (`contrato_id`, `abonaCapital`; D-2026-09-14-O, anexo).
 *    (Medido: toda cuota que emite el sistema nace `venta_unidad`, también el enganche; `anticipo_enganche` sólo entra por captura manual.)
 *  · `TIPOS_DE_COBRANZA_DE_VENTA` — «¿esto es lo COBRADO de la venta de una casa (enganche y mensualidades, sin el apartado)?»: el «cobrado» y la gráfica
 *    del tablero, «¿es cobranza de venta?» (`cuenta-elegible.ts`) y «¿exige casa?» (`ingresos.routes.ts`).
 *
 * Escribir cualquiera de las dos a mano es el defecto (candado T2.23/T2.24 de `docs/bodega-candados.json`).
 */
export const TIPOS_DE_COBRO_DE_CUOTA: readonly IngresoTipo[] = [INGRESO_TIPO.ANTICIPO_ENGANCHE, INGRESO_TIPO.VENTA_UNIDAD];
export const esTipoDeCobroDeCuota = (tipo: string | null | undefined): boolean => (TIPOS_DE_COBRO_DE_CUOTA as readonly string[]).includes(tipo ?? '');
export const TIPOS_DE_COBRANZA_DE_VENTA: readonly IngresoTipo[] = [INGRESO_TIPO.ANTICIPO_ENGANCHE, INGRESO_TIPO.VENTA_UNIDAD];
export const esTipoDeCobranzaDeVenta = (tipo: string | null | undefined): boolean => (TIPOS_DE_COBRANZA_DE_VENTA as readonly string[]).includes(tipo ?? '');

/** ingresos_registros.estatus — enum('BORRADOR','PENDIENTE_VALIDACION','PAGADO','RECHAZADO','CANCELADO') */
export const INGRESO_ESTATUS = {
  BORRADOR: 'BORRADOR',
  PENDIENTE_VALIDACION: 'PENDIENTE_VALIDACION',
  PAGADO: 'PAGADO',
  RECHAZADO: 'RECHAZADO',
  CANCELADO: 'CANCELADO',
} as const;
export type IngresoEstatus = (typeof INGRESO_ESTATUS)[keyof typeof INGRESO_ESTATUS];

/**
 * ingresos_registros.origen_ingreso — enum('manual_n1','portal_cliente_auto',
 * 'portal_cliente_manual','sistema_recurrente','sistema_mora') — MEDIDO en `02_DDL/DDL_v14_MASTER.sql` (columna `origen_ingreso`).
 *
 * 🔴 Ficha H (revisor externo, 2026-09-07): `PanelFiltrosIngresos.tsx`
 * (`ORIGEN_INGRESO_OPTIONS`) traía estos 4 valores a mano en producción — el candado
 * pre-commit no los veía porque van como VALORES DE UN OBJETO de opciones (`{ value:
 * 'sistema_recurrente', label: '...' }`), no como comparaciones `=== 'sistema_recurrente'`.
 * Los valores ya coincidían con el DDL (verificado, no había bug de negocio) — esto los
 * formaliza como constante única para que un cambio futuro en el ENUM no pueda
 * desincronizarse en silencio.
 */
export const ORIGEN_INGRESO = {
  MANUAL_N1: 'manual_n1',
  PORTAL_CLIENTE_AUTO: 'portal_cliente_auto',
  PORTAL_CLIENTE_MANUAL: 'portal_cliente_manual',
  SISTEMA_RECURRENTE: 'sistema_recurrente',
  /** I-203 · BUG-2224 fase 2 (Mig 100): el cobro de MORA que el sistema emite. La cartera lo
   *  distingue por este origen —no por el tipo `otros_ingresos`, que también se teclea a mano. */
  SISTEMA_MORA: 'sistema_mora',
} as const;
export type OrigenIngreso = (typeof ORIGEN_INGRESO)[keyof typeof ORIGEN_INGRESO];

/**
 * ingresos_registros.motivo_cancelacion (Mig 130, D-2026-10-09-I) — POR QUÉ se canceló un cobro. «devolver_cliente»: el dinero entró y se le devuelve
 * (si esa devolución muere, el cobro REVIVE como pagado, D-2026-10-07-B); «error_captura»: el dinero nunca existió (no revive). NULL = cancelado antes de
 * la Mig 130 (no revive: el lado seguro). Lo escribe `cancelarIngreso` en el MISMO UPDATE que lo cancela.
 */
export const INGRESO_MOTIVO_CANCELACION = {
  ERROR_CAPTURA: 'error_captura',
  DEVOLVER_CLIENTE: 'devolver_cliente',
} as const;
export type IngresoMotivoCancelacion = (typeof INGRESO_MOTIVO_CANCELACION)[keyof typeof INGRESO_MOTIVO_CANCELACION];

/** recibos.tipo — enum('apartado','enganche','mensualidad','liquidacion') */
export const RECIBO_TIPO = {
  APARTADO: 'apartado',
  ENGANCHE: 'enganche',
  MENSUALIDAD: 'mensualidad',
  LIQUIDACION: 'liquidacion',
} as const;
export type ReciboTipo = (typeof RECIBO_TIPO)[keyof typeof RECIBO_TIPO];

/** planes_pago.tipo_pago — enum('enganche','mensualidad','liquidacion','penalizacion','ajuste') */
export const PLAN_PAGO_TIPO = {
  ENGANCHE: 'enganche',
  MENSUALIDAD: 'mensualidad',
  LIQUIDACION: 'liquidacion',
  PENALIZACION: 'penalizacion',
  AJUSTE: 'ajuste',
} as const;
export type PlanPagoTipo = (typeof PLAN_PAGO_TIPO)[keyof typeof PLAN_PAGO_TIPO];

/**
 * W5 · B-5 — el NOMBRE de cada tipo de cuota y «¿qué cuota es?» («Mensualidad 12 de 48», «Enganche — parte 1 de 2»). UN solo
 * lugar para el servidor (estado de cuenta, PDF, recibo) y la pantalla (cartera): antes vivía sólo en el front
 * (`CarteraPorVencimiento.tsx`) y el PDF no tenía cómo decir a qué cuota entró un pago. Bodega T2 #12 (la posición y el total
 * los cuenta el servidor: `posicionCuotaSQL`).
 */
export const TIPO_DE_CUOTA_LABEL: Record<string, string> = {
  [PLAN_PAGO_TIPO.ENGANCHE]:     'Enganche',
  [PLAN_PAGO_TIPO.MENSUALIDAD]:  'Mensualidad',
  [PLAN_PAGO_TIPO.LIQUIDACION]:  'Liquidación',
  [PLAN_PAGO_TIPO.PENALIZACION]: 'Penalización',
  [PLAN_PAGO_TIPO.AJUSTE]:       'Ajuste',
};

export function nombreDeLaCuota(c: { tipo_pago: string; orden_en_tipo: number; total_en_tipo: number }): string {
  const { tipo_pago: tipo, orden_en_tipo: k, total_en_tipo: n } = c;
  if (tipo === PLAN_PAGO_TIPO.ENGANCHE && n > 1) return `Enganche — parte ${k} de ${n}`;
  if (tipo === PLAN_PAGO_TIPO.MENSUALIDAD) return `Mensualidad ${k} de ${n}`;
  return TIPO_DE_CUOTA_LABEL[tipo] ?? 'Ajuste';
}

/** planes_pago.estado — enum('pendiente','pagado','vencido','cancelado','restructurado') */
export const PLAN_PAGO_ESTADO = {
  PENDIENTE: 'pendiente',
  PAGADO: 'pagado',
  VENCIDO: 'vencido',
  CANCELADO: 'cancelado',
  RESTRUCTURADO: 'restructurado',
} as const;
export type PlanPagoEstado = (typeof PLAN_PAGO_ESTADO)[keyof typeof PLAN_PAGO_ESTADO];

/** Tipo de gestión de cobranza — concepto de negocio, sin columna ENUM propia en BD */
export const COBRANZA_TIPO = {
  RECORDATORIO: 'recordatorio',
  AVISO_MORA: 'aviso_mora',
  DEMANDA: 'demanda',
  ACUERDO_PAGO: 'acuerdo_pago',
} as const;
export type CobranzaTipo = (typeof COBRANZA_TIPO)[keyof typeof COBRANZA_TIPO];

/** cobranza_registros.estado — enum('PENDIENTE','PAGADO','AUTORIZADO','VENCIDO','CANCELADO') */
export const COBRANZA_ESTATUS = {
  PENDIENTE: 'PENDIENTE',
  PAGADO: 'PAGADO',
  AUTORIZADO: 'AUTORIZADO',
  VENCIDO: 'VENCIDO',
  CANCELADO: 'CANCELADO',
} as const;
export type CobranzaEstatus = (typeof COBRANZA_ESTATUS)[keyof typeof COBRANZA_ESTATUS];

/** pagos_comprobantes.estado — enum('pendiente','auto_conciliado','conciliado','rechazado') */
export const COMPROBANTE_ESTADO = {
  PENDIENTE: 'pendiente',
  AUTO_CONCILIADO: 'auto_conciliado',
  CONCILIADO: 'conciliado',
  RECHAZADO: 'rechazado',
} as const;
export type ComprobanteEstado = (typeof COMPROBANTE_ESTADO)[keyof typeof COMPROBANTE_ESTADO];

/** pagos_comprobantes.origen_carga — enum('portal_cliente','erp_manual','portal_broker') */
export const COMPROBANTE_ORIGEN = {
  PORTAL_CLIENTE: 'portal_cliente',
  ERP_MANUAL:     'erp_manual',
  PORTAL_BROKER:  'portal_broker',
} as const;
export type ComprobanteOrigen = (typeof COMPROBANTE_ORIGEN)[keyof typeof COMPROBANTE_ORIGEN];

/** Método de pago en comprobante — concepto, sin ENUM propio en BD */
export const COMPROBANTE_TIPO = {
  TRANSFERENCIA: 'transferencia',
  CHEQUE: 'cheque',
  EFECTIVO: 'efectivo',
  TARJETA: 'tarjeta',
  OTRO: 'otro',
} as const;
export type ComprobanteTipo = (typeof COMPROBANTE_TIPO)[keyof typeof COMPROBANTE_TIPO];

// ─────────────────────────────────────────────────────────────────────────────
// M02 — EGRESOS
// ─────────────────────────────────────────────────────────────────────────────

/** egresos_registros.tipo_egreso — enum('construccion','operativos','servicio_deuda','gastos_credito','impuestos_honorarios','otros','retiro_utilidades','devolucion_aportacion','prestamo_accionista','gasto_paga_accionista','reparacion_garantia','devolucion_cliente') */
export const EGRESO_TIPO = {
  CONSTRUCCION: 'construccion',
  OPERATIVOS: 'operativos',
  SERVICIO_DEUDA: 'servicio_deuda',
  GASTOS_CREDITO: 'gastos_credito',
  IMPUESTOS_HONORARIOS: 'impuestos_honorarios',
  OTROS: 'otros',
  RETIRO_UTILIDADES: 'retiro_utilidades',
  DEVOLUCION_APORTACION: 'devolucion_aportacion',
  PRESTAMO_ACCIONISTA: 'prestamo_accionista',
  GASTO_PAGA_ACCIONISTA: 'gasto_paga_accionista',
  REPARACION_GARANTIA: 'reparacion_garantia',
  DEVOLUCION_CLIENTE: 'devolucion_cliente',
} as const;
export type EgresoTipo = (typeof EGRESO_TIPO)[keyof typeof EGRESO_TIPO];

/** egresos_registros.tipo_deuda — enum('capital','interes','comision') — componente del pago cuando credito_id IS NOT NULL */
export const EGRESO_TIPO_DEUDA = {
  CAPITAL: 'capital',
  INTERES: 'interes',
  COMISION: 'comision',
} as const;
export type EgresoTipoDeuda = (typeof EGRESO_TIPO_DEUDA)[keyof typeof EGRESO_TIPO_DEUDA];

/** egresos_registros.estatus — enum('BORRADOR','PENDIENTE_N1','PENDIENTE_N2','AUTORIZADO','PAGADO','RECHAZADO','CANCELADO') */
export const EGRESO_ESTATUS = {
  BORRADOR: 'BORRADOR',
  PENDIENTE_N1: 'PENDIENTE_N1',
  PENDIENTE_N2: 'PENDIENTE_N2',
  AUTORIZADO: 'AUTORIZADO',
  PAGADO: 'PAGADO',
  RECHAZADO: 'RECHAZADO',
  CANCELADO: 'CANCELADO',
} as const;
export type EgresoEstatus = (typeof EGRESO_ESTATUS)[keyof typeof EGRESO_ESTATUS];

/**
 * egresos_registros.origen_egreso — enum('manual_n1','portal_proveedor_auto','portal_proveedor_manual','sistema_recurrente','sistema_comision')
 * Mig 69 (BUG-405) agrega `sistema_comision`: `comisiones.service.ts` y `crm.service.ts`
 * reusaban `sistema_recurrente` (el valor de CR02) para las comisiones de broker porque no
 * había un valor propio — mezclaba comisiones con gastos recurrentes de verdad en cualquier
 * consumidor de esta columna (p. ej. `INV-GTO-17` en `scripts/invariantes/reglas/gasto.ts`,
 * que agrupa por `origen_egreso = 'sistema_recurrente'` para detectar pagos recurrentes
 * duplicados — con la mezcla, dos comisiones legítimas del mismo mes podían leerse como el
 * mismo compromiso repetido).
 */
export const ORIGEN_EGRESO = {
  MANUAL_N1: 'manual_n1',
  PORTAL_PROVEEDOR_AUTO: 'portal_proveedor_auto',
  PORTAL_PROVEEDOR_MANUAL: 'portal_proveedor_manual',
  SISTEMA_RECURRENTE: 'sistema_recurrente',
  SISTEMA_COMISION: 'sistema_comision',
} as const;
export type OrigenEgreso = (typeof ORIGEN_EGRESO)[keyof typeof ORIGEN_EGRESO];

/** egresos_registros.recurrencia_frecuencia — enum('quincenal','mensual','bimestral','trimestral','semestral','anual') */
export const EGRESO_RECURRENCIA = {
  QUINCENAL: 'quincenal',
  MENSUAL: 'mensual',
  BIMESTRAL: 'bimestral',
  TRIMESTRAL: 'trimestral',
  SEMESTRAL: 'semestral',
  ANUAL: 'anual',
} as const;
export type EgresoRecurrencia = (typeof EGRESO_RECURRENCIA)[keyof typeof EGRESO_RECURRENCIA];

/** «Al mes» de un gasto que se repite — cuántas veces por MES cae cada frecuencia (BUG-3062 · I-240, bodega T1·13). El servidor lo usa en `alMesSQL`
 *  (helpers/gasto-recurrente-al-mes.ts); la pantalla lo importa de aquí en vez de escribir su propia tabla. */
export const VECES_AL_MES: Readonly<Record<EgresoRecurrencia, number>> = {
  [EGRESO_RECURRENCIA.QUINCENAL]:  2,
  [EGRESO_RECURRENCIA.MENSUAL]:    1,
  [EGRESO_RECURRENCIA.BIMESTRAL]:  1 / 2,
  [EGRESO_RECURRENCIA.TRIMESTRAL]: 1 / 3,
  [EGRESO_RECURRENCIA.SEMESTRAL]:  1 / 6,
  [EGRESO_RECURRENCIA.ANUAL]:      1 / 12,
};

/** D-2026-09-04 — caracteres mínimos del motivo de rechazo de un egreso, y de pedir/reabrir un mes (mismo mínimo que la nota de bypass, §8.5).
 *  Lo exige `egresos.routes.ts`; la pantalla lo importa de aquí. */
export const MINIMO_MOTIVO_RECHAZO = 30;

/** A quién avisa «egreso autorizado» (`EGRESO_AUTORIZADO`, egresos.routes.ts): los que la ficha nombra al autorizar como excepción. */
export const ROLES_AVISO_EGRESO_AUTORIZADO: readonly UsuarioRol[] = [USUARIO_ROL.CEO, USUARIO_ROL.CFO, USUARIO_ROL.CONTABILIDAD_CXP];

/** I-257 (BUG-408) — cuántos días hacia cada lado cuenta un pago como «parecido» (Mau, D-2026-10-01-G.5: 30 días). Lo usa `buscarPagoParecido`
 *  (egresos.service.ts); la pantalla lo importa de aquí. */
export const DIAS_PAGO_PARECIDO = 30;

/** cuentas_contables.tipo — enum('activo','pasivo','capital','ingreso','egreso','orden') */
export const CUENTA_CONTABLE_TIPO = {
  ACTIVO:   'activo',
  PASIVO:   'pasivo',
  CAPITAL:  'capital',
  INGRESO:  'ingreso',
  EGRESO:   'egreso',
  ORDEN:    'orden',
} as const;
export type CuentaContableTipo = (typeof CUENTA_CONTABLE_TIPO)[keyof typeof CUENTA_CONTABLE_TIPO];

// ─────────────────────────────────────────────────────────────────────────────
// M03 — BANCOS Y TESORERÍA
// ─────────────────────────────────────────────────────────────────────────────

/** cuentas_banco.estado — enum('Activa','Inactiva') */
export const CUENTA_BANCO_ESTADO = {
  ACTIVA:   'Activa',
  INACTIVA: 'Inactiva',
} as const;
export type CuentaBancoEstado = (typeof CUENTA_BANCO_ESTADO)[keyof typeof CUENTA_BANCO_ESTADO];

/**
 * Mínimo de caracteres de un texto obligatorio — UN dueño (barrido S2, 20-oct; nombres de W1, ORQ 20-oct): lo leen el servidor (zod y servicios) y la pantalla.
 * `LARGO_MINIMO_MOTIVO_CORTO` (10): el motivo al rechazar un cobro (la pantalla ya lo pedía en 10; el servidor aceptaba 1 — decisión del ORQ 20-oct, revocable: nadie la tenía dicha).
 * `LARGO_MINIMO_NOTA` (30): la nota que justifica saltarse un límite o resolver la mora (HL-04/HL-10: 29 rebota, 30 pasa).
 */
export const LARGO_MINIMO_NOTA = 30;
export const LARGO_MINIMO_MOTIVO_CORTO = 10;
/** `LARGO_MINIMO_MOTIVO_TICKET` (5): el motivo/nota al cancelar o cerrar un ticket y al cancelar una asignación de postventa — el piso del trigger `trg_ticket_transicion_valida` (`LENGTH(TRIM(nota_cancelacion)) < 5`); la pantalla lo pide igual. */
export const LARGO_MINIMO_MOTIVO_TICKET = 5;

/**
 * egresos_registros.metodo_pago · catalogo_presupuesto.metodo_pago
 * — enum('transferencia','cheque','efectivo')
 *
 * `efectivo` no es un detalle contable: decide A QUÉ CUENTA aterriza el cobro
 * (a la caja del proyecto, no a un banco). Ver `resolverCuentaDeCobro`.
 */
export const METODO_PAGO = {
  TRANSFERENCIA: 'transferencia',
  CHEQUE:        'cheque',
  EFECTIVO:      'efectivo',
} as const;
export type MetodoPago = (typeof METODO_PAGO)[keyof typeof METODO_PAGO];

/**
 * ingresos_registros.metodo_pago — enum('transferencia','cheque','efectivo','otro')
 *
 * 🔴 NO es el mismo ENUM que el de egresos: el de INGRESOS tiene un cuarto valor,
 * `otro`. MEDIDO 2026-08-19 contra `erp_test` y contra PRODUCCIÓN, las dos iguales.
 * Se descubrió porque `dictionary-consistency` lo cazó al declararlos como uno solo.
 * El schema de Drizzle también lo tenía con 3 — drift viejo, corregido en el mismo
 * cambio.
 */
export const METODO_PAGO_INGRESO = {
  ...METODO_PAGO,
  OTRO: 'otro',
} as const;
export type MetodoPagoIngreso = (typeof METODO_PAGO_INGRESO)[keyof typeof METODO_PAGO_INGRESO];

/** Cómo se pagó un cobro (`ingresos_registros.metodo_pago`), en palabras de la pantalla. Rebote del cerrador (25-sep): la lista de
 *  cuotas lo pintaba con `replace('_', ' ')`. Vivía en `CarteraPorVencimiento.tsx` (borrada en S2·R4); aquí está su dueño. */
export const METODO_DE_COBRO_LABEL: Record<string, string> = {
  [METODO_PAGO_INGRESO.TRANSFERENCIA]: 'Transferencia',
  [METODO_PAGO_INGRESO.CHEQUE]:        'Cheque',
  [METODO_PAGO_INGRESO.EFECTIVO]:      'Efectivo',
  [METODO_PAGO_INGRESO.OTRO]:          'Otro',
  varios:                              'Varios',   // E-15 — una cuota pagada con cobros de métodos distintos
};

/** cuentas_banco.proposito_cuenta — enum('Recaudadora','Operativa','Reserva','Fideicomiso','Nomina','Cajas_Chicas','Efectivo') */
export const CUENTA_PROPOSITO = {
  RECAUDADORA: 'Recaudadora',
  OPERATIVA:   'Operativa',
  RESERVA:     'Reserva',
  FIDEICOMISO: 'Fideicomiso',
  NOMINA:      'Nomina',
  CAJAS_CHICAS: 'Cajas_Chicas',
  /**
   * La caja de EFECTIVO del proyecto. Cuenta virtual: el dinero existe pero no está
   * en ningún banco todavía. Mau, 2026-08-19: «lo mismo con efectivo, si se marca
   * efectivo así iría, y éste se aplicaría a la cuenta de efectivo».
   * Sin ella, exigir cuenta en todo cobro volvería imposible el cobro en efectivo.
   */
  EFECTIVO:     'Efectivo',
} as const;
export type CuentaProposito = (typeof CUENTA_PROPOSITO)[keyof typeof CUENTA_PROPOSITO];

/** cuentas_banco.tipo_cuenta — enum('Cheques','Ahorro','Inversion') */
export const CUENTA_TIPO = {
  CHEQUES: 'Cheques',
  AHORRO: 'Ahorro',
  INVERSION: 'Inversion',
} as const;
export type CuentaTipo = (typeof CUENTA_TIPO)[keyof typeof CUENTA_TIPO];

/** movimientos_bancarios.tipo_movimiento — enum('abono','cargo') */
export const MOVIMIENTO_TIPO = {
  ABONO: 'abono',
  CARGO: 'cargo',
} as const;
export type MovimientoTipo = (typeof MOVIMIENTO_TIPO)[keyof typeof MOVIMIENTO_TIPO];

/** movimientos_bancarios.origen — enum('importado','manual','transferencia_erp') (DDL `movimientos_bancarios.origen`) */
export const MOVIMIENTO_ORIGEN = {
  IMPORTADO: 'importado',
  MANUAL: 'manual',
  TRANSFERENCIA_ERP: 'transferencia_erp',
} as const;
export type MovimientoOrigen = (typeof MOVIMIENTO_ORIGEN)[keyof typeof MOVIMIENTO_ORIGEN];

/** movimientos_bancarios.estado_conciliacion — enum('PENDIENTE','AUTO_CONCILIADO','CONCILIADO_MANUAL','NO_IDENTIFICADO','EXCLUIDO') */
export const MOVIMIENTO_ESTATUS = {
  PENDIENTE: 'PENDIENTE',
  AUTO_CONCILIADO: 'AUTO_CONCILIADO',
  CONCILIADO_MANUAL: 'CONCILIADO_MANUAL',
  NO_IDENTIFICADO: 'NO_IDENTIFICADO',
  EXCLUIDO: 'EXCLUIDO',
} as const;
export type MovimientoEstatus = (typeof MOVIMIENTO_ESTATUS)[keyof typeof MOVIMIENTO_ESTATUS];

/** conciliacion_bancaria.estatus — enum('Abierta','Cerrada','Auditada') */
export const CONCILIACION_ESTATUS = {
  ABIERTA: 'Abierta',
  CERRADA: 'Cerrada',
  AUDITADA: 'Auditada',
} as const;
export type ConciliacionEstatus = (typeof CONCILIACION_ESTATUS)[keyof typeof CONCILIACION_ESTATUS];

/** cuentas_bancarias_cambios_pendientes.estado — enum('propuesto','aprobado','rechazado') */
export const IB2_CAMBIO_ESTADO = {
  PROPUESTO: 'propuesto',
  APROBADO:  'aprobado',
  RECHAZADO: 'rechazado',
} as const;
export type Ib2CambioEstado = (typeof IB2_CAMBIO_ESTADO)[keyof typeof IB2_CAMBIO_ESTADO];

/** conciliacion_parcial.estado — enum('pendiente','confirmado','rechazado') */
export const CONCILIACION_PARCIAL_ESTADO = {
  PENDIENTE:  'pendiente',
  CONFIRMADO: 'confirmado',
  RECHAZADO:  'rechazado',
} as const;
export type ConciliacionParcialEstado = (typeof CONCILIACION_PARCIAL_ESTADO)[keyof typeof CONCILIACION_PARCIAL_ESTADO];

/**
 * conciliacion_transacciones_erp.tipo_transaccion_erp — discriminador del algoritmo de
 * conciliación (nunca es 'transferencia', ver CONCEPTOS_SIN_COLUMNA en
 * dictionary-consistency.test.ts), Y `movimientos_bancarios.tipo_transaccion_erp` — enum('ingreso',
 * 'egreso','transferencia') REAL (DDL `:2808`). I-Pagos-E-r1/E-r2 revisión adversarial 2ª
 * ronda (R2-9/§25 REGLA 1) — `TRANSFERENCIA` se agrega aquí porque la columna de BD sí la
 * admite; el candado de cierre de mes sólo compara contra `.EGRESO`/`.INGRESO`, nunca contra
 * el valor completo del ENUM, así que agregar el tercer miembro no cambia ningún filtro.
 */
export const TRANSACCION_TIPO = {
  INGRESO: 'ingreso',
  EGRESO:  'egreso',
  TRANSFERENCIA: 'transferencia',
} as const;
export type TransaccionTipo = (typeof TRANSACCION_TIPO)[keyof typeof TRANSACCION_TIPO];

/**
 * I-Pagos-E-r7 (D-2026-09-10-W) — SAT: atributo `MetodoPago` de la raíz `Comprobante` del
 * CFDI XML. NO es columna de BD (como `TRANSACCION_TIPO` arriba): se lee del propio archivo
 * XML que la persona sube a `POST /egresos/:id/documentos`, nunca se persiste — decide si
 * la MISMA factura repetida en dos egresos es una parcialidad legítima (PPD) o un duplicado
 * (cualquier otro caso, incluido PUE). Entra a `ENUM_MAP` como `cfdi_xml.metodo_pago` bajo
 * `CONCEPTOS_SIN_COLUMNA` (mismo patrón que `conciliacion_transacciones_erp.tipo_transaccion_erp`).
 */
export const CFDI_METODO_PAGO = {
  PPD: 'PPD', // Pago en Parcialidades o Diferido
  PUE: 'PUE', // Pago en una sola Exhibición
} as const;
export type CfdiMetodoPago = (typeof CFDI_METODO_PAGO)[keyof typeof CFDI_METODO_PAGO];

/** transferencia_interna_prestamo.estatus — enum('pendiente','aprobado_nivel1','aprobado_nivel2','ejecutado','desembolsado','pagado','cancelado') */
export const TRANSFERENCIA_INTERNA_ESTATUS = {
  PENDIENTE:        'pendiente',
  APROBADO_NIVEL1:  'aprobado_nivel1',
  APROBADO_NIVEL2:  'aprobado_nivel2',
  EJECUTADO:        'ejecutado',
  DESEMBOLSADO:     'desembolsado',
  PAGADO:           'pagado',
  CANCELADO:        'cancelado',
} as const;
export type TransferenciaInternaEstatus = (typeof TRANSFERENCIA_INTERNA_ESTATUS)[keyof typeof TRANSFERENCIA_INTERNA_ESTATUS];

// ─────────────────────────────────────────────────────────────────────────────
// M04 — PROYECTOS + MAPA INTERACTIVO
// ─────────────────────────────────────────────────────────────────────────────

/**
 * unidades.estado_venta — enum('Disponible','En Negociacion','Apartada','Vendida','Entregada','En Cancelacion')
 * CRÍTICO: el valor correcto es 'Apartada' (femenino), NO 'Apartado'.
 * cr10.ts:71 usa 'Apartado' → bug INC-001: cron de expiración no funciona.
 */
export const UNIDAD_ESTADO_VENTA = {
  DISPONIBLE: 'Disponible',
  EN_NEGOCIACION: 'En Negociacion',
  APARTADA: 'Apartada',   // ← femenino; cr10.ts usa 'Apartado' — BUG INC-001
  VENDIDA: 'Vendida',
  ENTREGADA: 'Entregada',
  /** Mig 139 (D-2026-10-15-F) — el contrato se canceló y falta el documento firmado o la devolución; se puede cotizar y apartar con aviso. */
  EN_CANCELACION: 'En Cancelacion',
} as const;
export type UnidadEstadoVenta = (typeof UNIDAD_ESTADO_VENTA)[keyof typeof UNIDAD_ESTADO_VENTA];

/**
 * BUG-2171 (I-205) — «¿esta unidad cuenta como VENDIDA?» tiene UNA respuesta: Vendida o
 * Entregada (BUG-1741 alineó los 6 sitios; aquí se vuelve un solo dueño). Motor SQL:
 * `unidadVendidaSQL()` en backend/src/services/unidad-vigente.ts.
 */
export const UNIDAD_ESTADOS_VENDIDA: readonly UnidadEstadoVenta[] = [
  UNIDAD_ESTADO_VENTA.VENDIDA,
  UNIDAD_ESTADO_VENTA.ENTREGADA,
] as const;
export function esUnidadVendida(estado: string | null | undefined): boolean {
  return (UNIDAD_ESTADOS_VENDIDA as readonly string[]).includes(estado ?? '');
}

/**
 * BUG-4145/4161 — «¿qué unidad se puede volver a ofrecer?» tiene UNA respuesta: la libre y la que está en proceso de cancelación
 * (Mau, 15-oct 11:53: «posible liberación para saber que tenemos esa unidad para vender»). El servidor la usa para contar «por vender»
 * y la pantalla para ofrecerla (combo, cotizador, mapa del broker): ninguna pantalla vuelve a filtrar `Disponible` a mano.
 */
export const UNIDAD_ESTADOS_POR_VENDER: readonly UnidadEstadoVenta[] = [
  UNIDAD_ESTADO_VENTA.DISPONIBLE,
  UNIDAD_ESTADO_VENTA.EN_CANCELACION,
] as const;
export function esUnidadPorVender(estado: string | null | undefined): boolean {
  return (UNIDAD_ESTADOS_POR_VENDER as readonly string[]).includes(estado ?? '');
}

/**
 * BUG-4174 — «¿se le puede hacer una cotización a esta unidad?»: lo que se puede ofrecer (`esUnidadPorVender`) más la que otro vendedor está
 * viendo (En Negociación: D-2026-09-11-B, «otro vendedor cotizando no la bloquea»). Apartada, Vendida y Entregada ya son de otra persona.
 */
export function esUnidadCotizable(estado: string | null | undefined): boolean {
  return esUnidadPorVender(estado) || estado === UNIDAD_ESTADO_VENTA.EN_NEGOCIACION;
}

/** unidades.tipo — enum('departamento','local','bodega','oficina','consultorio','otro','lote','terreno','casa') */
export const UNIDAD_TIPO = {
  DEPARTAMENTO: 'departamento',
  LOCAL: 'local',
  BODEGA: 'bodega',
  OFICINA: 'oficina',
  CONSULTORIO: 'consultorio',
  OTRO: 'otro',
  LOTE: 'lote',
  TERRENO: 'terreno',
  /** Mig 65 — una casa de fraccionamiento. Antes se capturaba como 'otro'. */
  CASA: 'casa',
} as const;
export type UnidadTipo = (typeof UNIDAD_TIPO)[keyof typeof UNIDAD_TIPO];

/** proyectos.tipo_proyecto — enum('torre','plaza_comercial','bodega','hotel','otro','corporativo','desarrollo_horizontal') */
export const PROYECTO_TIPO = {
  TORRE:           'torre',
  PLAZA_COMERCIAL: 'plaza_comercial',
  BODEGA:          'bodega',
  HOTEL:           'hotel',
  OTRO:            'otro',
  CORPORATIVO:     'corporativo',
  /**
   * Mig 65 — el paraguas horizontal. Mau: «acá entran lotes, terrenos o casas».
   * Se distingue de 'torre' porque no se apila: la unidad no tiene piso, tiene manzana.
   */
  DESARROLLO_HORIZONTAL: 'desarrollo_horizontal',
} as const;
export type ProyectoTipo = (typeof PROYECTO_TIPO)[keyof typeof PROYECTO_TIPO];

// ─── BUG-4148 (W5, 15-oct) — «Corregir el tipo» del proyecto ─────────────────────────────────────────────────────────
// Diseño: `design_handoff_unidades_cotizador 2/README.md:91` y `casa-unidades-app.jsx:273-276,348-380`. El diseño lista 7 tipos; tres no
// existen en la base (Uso mixto · Industrial · Remodelación) y manda lo nuestro (D-2026-10-01-A pt. 5): se ofrecen los de la base que son de un
// proyecto VENDIBLE, con el rótulo del diseño donde lo hay. `corporativo` NO se corrige jamás: es la oficina (overhead, cajón de gastos) y deja de ser
// vendible — pasar un proyecto a o desde ahí cambia de quién es el dinero (ORQ, 15-oct).
export const TIPOS_DE_PROYECTO_CORREGIBLES = [
  PROYECTO_TIPO.TORRE, PROYECTO_TIPO.DESARROLLO_HORIZONTAL, PROYECTO_TIPO.PLAZA_COMERCIAL,
  PROYECTO_TIPO.BODEGA, PROYECTO_TIPO.HOTEL, PROYECTO_TIPO.OTRO,
] as const;

/** El rótulo de cada tipo en pantalla — UN solo lugar (antes: `IdentidadProyectoTab` tenía los suyos). */
export const ROTULO_TIPO_PROYECTO: Record<ProyectoTipo, string> = {
  [PROYECTO_TIPO.TORRE]:                 'Edificio vertical',
  [PROYECTO_TIPO.DESARROLLO_HORIZONTAL]: 'Desarrollo horizontal',
  [PROYECTO_TIPO.PLAZA_COMERCIAL]:       'Comercial',
  [PROYECTO_TIPO.BODEGA]:                'Bodega',
  [PROYECTO_TIPO.HOTEL]:                 'Hotel',
  [PROYECTO_TIPO.OTRO]:                  'Otro',
  [PROYECTO_TIPO.CORPORATIVO]:           'Corporativo',
};

/**
 * Qué tipos de unidad se ponen PRIMERO al dar de alta, según el tipo del proyecto (`TIPOS_DE` del diseño, `casa-unidades-data.jsx:47-49`).
 * SUGIERE, NO RESTRINGE (D-2026-09-16-F «el sistema menciona, no frena»): los demás siguen disponibles en `otros`; un local en un
 * fraccionamiento horizontal se vale.
 */
export function tiposDeUnidadDe(tipoProyecto: string | null | undefined): { sugeridos: UnidadTipo[]; otros: UnidadTipo[] } {   // revision-previa-ok: copia FÍSICA obligatoria de packages/shared/types/erp-dictionary.ts (dictionary-parity-backend-shared.test.ts exige que sean idénticas); `tiposDeUnidadDe` la usa la pantalla por @erp/shared, no es código muerto.
  const sugeridos: UnidadTipo[] = tipoProyecto === PROYECTO_TIPO.DESARROLLO_HORIZONTAL
    ? [UNIDAD_TIPO.CASA, UNIDAD_TIPO.LOTE, UNIDAD_TIPO.TERRENO, UNIDAD_TIPO.OTRO]
    : [UNIDAD_TIPO.DEPARTAMENTO, UNIDAD_TIPO.LOCAL, UNIDAD_TIPO.BODEGA, UNIDAD_TIPO.OFICINA, UNIDAD_TIPO.CONSULTORIO, UNIDAD_TIPO.OTRO];
  return { sugeridos, otros: (Object.values(UNIDAD_TIPO) as UnidadTipo[]).filter((t) => !sugeridos.includes(t)) };
}

/** estimaciones_proyecto.estatus — enum('borrador','enviada','autorizada','pagada','rechazada','cancelada') */
export const ESTIMACION_ESTADO = {
  BORRADOR: 'borrador',
  ENVIADA: 'enviada',
  AUTORIZADA: 'autorizada',
  PAGADA: 'pagada',
  RECHAZADA: 'rechazada',
  CANCELADA: 'cancelada',
} as const;
export type EstimacionEstado = (typeof ESTIMACION_ESTADO)[keyof typeof ESTIMACION_ESTADO];

/**
 * «¿De qué color se pinta cada estado de una unidad?» tiene UN dueño: éste (I-276). Los colores del mapa de M04 §19 y el naranja
 * de «En proceso de cancelación» (`design_handoff_cancelar_con_penalizacion/TOKENS.md`: el único hex nuevo que permite).
 * Lo leen la casa de unidades (`ESTADO_UNIDAD`), el mapa del broker, el mapa del proyecto y el tablero de Ventas; ninguno
 * vuelve a escribir el hex. (Reemplaza a `SHAPE_COLOR`, de 4 estados y sin lectores.)
 */
export const UNIDAD_ESTADO_COLOR: Record<UnidadEstadoVenta, string> = {
  [UNIDAD_ESTADO_VENTA.DISPONIBLE]:     '#22C55E',
  [UNIDAD_ESTADO_VENTA.EN_NEGOCIACION]: '#3B82F6',
  [UNIDAD_ESTADO_VENTA.APARTADA]:       '#EAB308',
  [UNIDAD_ESTADO_VENTA.VENDIDA]:        '#EF4444',
  [UNIDAD_ESTADO_VENTA.EN_CANCELACION]: '#F97316',
  [UNIDAD_ESTADO_VENTA.ENTREGADA]:      '#6B7280',
};

/**
 * I-276 — la TINTA (color del texto sobre el relleno) de cada estado de unidad: segunda mitad de la misma paleta, mismo dueño que
 * `UNIDAD_ESTADO_COLOR`. La casa de unidades (`ESTADO_UNIDAD`) la lee; nadie escribe el hex en una pantalla.
 */
export const UNIDAD_ESTADO_TINTA: Record<UnidadEstadoVenta, string> = {
  [UNIDAD_ESTADO_VENTA.DISPONIBLE]:     '#15803D',
  [UNIDAD_ESTADO_VENTA.EN_NEGOCIACION]: '#1D4ED8',
  [UNIDAD_ESTADO_VENTA.APARTADA]:       '#A16207',
  [UNIDAD_ESTADO_VENTA.VENDIDA]:        '#B91C1C',
  [UNIDAD_ESTADO_VENTA.EN_CANCELACION]: '#C2410C',
  [UNIDAD_ESTADO_VENTA.ENTREGADA]:      '#4B5563',
};

/** suplementos_presupuesto.estado — enum('pendiente','autorizado','rechazado') */
export const SUPLEMENTO_ESTADO = {
  PENDIENTE:  'pendiente',
  AUTORIZADO: 'autorizado',
  RECHAZADO:  'rechazado',
} as const;
export type SuplementoEstado = (typeof SUPLEMENTO_ESTADO)[keyof typeof SUPLEMENTO_ESTADO];

/** alertas_proyecto.estado — enum('activa','disparada','completada','vencida','descartada') */
export const ALERTA_PROYECTO_ESTADO = {
  ACTIVA:     'activa',
  DISPARADA:  'disparada',
  COMPLETADA: 'completada',
  VENCIDA:    'vencida',
  DESCARTADA: 'descartada',
} as const;
export type AlertaProyectoEstado = (typeof ALERTA_PROYECTO_ESTADO)[keyof typeof ALERTA_PROYECTO_ESTADO];

/** alertas_proyecto.tipo — incluyendo M07 cronograma */
export const ALERTA_PROYECTO_TIPO = {
  CONTRATACION:         'contratacion',
  FINALIZACION:         'finalizacion',
  VENCIMIENTO_LICENCIA: 'vencimiento_licencia',
  VARIANZA_PRESUPUESTO: 'varianza_presupuesto',
  ANTICIPO_PROVEEDOR:   'anticipo_proveedor',
  INICIO_PARTIDA:       'inicio_partida',
  RETRASO_DETECTADO:    'retraso_detectado',
} as const;
export type AlertaProyectoTipo = (typeof ALERTA_PROYECTO_TIPO)[keyof typeof ALERTA_PROYECTO_TIPO];

/** alertas_proyecto.prioridad */
export const ALERTA_PROYECTO_PRIORIDAD = {
  BAJA:    'baja',
  MEDIA:   'media',
  ALTA:    'alta',
  CRITICA: 'critica',
} as const;
export type AlertaProyectoPrioridad = (typeof ALERTA_PROYECTO_PRIORIDAD)[keyof typeof ALERTA_PROYECTO_PRIORIDAD];

/** estado_cronograma — valor calculado en GET /obra/cronograma (no columna de BD) */
export const CRONOGRAMA_ESTADO = {
  SIN_INICIAR: 'sin_iniciar',
  COMPLETADO:  'completado',
  ATRASADO:    'atrasado',
  ADELANTADO:  'adelantado',
  EN_TIEMPO:   'en_tiempo',
} as const;
export type CronogramaEstado = (typeof CRONOGRAMA_ESTADO)[keyof typeof CRONOGRAMA_ESTADO];

// ─────────────────────────────────────────────────────────────────────────────
// M05 — CRÉDITOS Y SOCIOS
// ─────────────────────────────────────────────────────────────────────────────

/** creditos.tipo_credito — enum('puente','mezzanine','otro') */
export const CREDITO_TIPO = {
  PUENTE: 'puente',
  MEZZANINE: 'mezzanine',
  OTRO: 'otro',
} as const;
export type CreditoTipo = (typeof CREDITO_TIPO)[keyof typeof CREDITO_TIPO];

/** creditos.tipo_linea — enum('simple','revolvente') */
export const CREDITO_LINEA = {
  SIMPLE: 'simple',
  REVOLVENTE: 'revolvente',
} as const;
export type CreditoLinea = (typeof CREDITO_LINEA)[keyof typeof CREDITO_LINEA];

/** creditos.estatus — enum('activo','vencido','liquidado','cancelado','refinanciado') */
export const CREDITO_ESTADO = {
  ACTIVO: 'activo',
  VENCIDO: 'vencido',
  LIQUIDADO: 'liquidado',
  CANCELADO: 'cancelado',
  REFINANCIADO: 'refinanciado',
} as const;
export type CreditoEstado = (typeof CREDITO_ESTADO)[keyof typeof CREDITO_ESTADO];

/** Tipo de tasa — concepto financiero, sin ENUM propio en BD */
export const CREDITO_TASA_TIPO = {
  FIJA: 'fija',
  VARIABLE: 'variable',
} as const;
export type CreditoTasaTipo = (typeof CREDITO_TASA_TIPO)[keyof typeof CREDITO_TASA_TIPO];

/** aportaciones_socios.estatus — enum('pendiente','registrado','conciliado') */
export const APORTACION_ESTADO = {
  PENDIENTE: 'pendiente',
  REGISTRADO: 'registrado',
  CONCILIADO: 'conciliado',
} as const;
export type AportacionEstado = (typeof APORTACION_ESTADO)[keyof typeof APORTACION_ESTADO];

/** ministracion_credito.estatus — enum('planificada','depositada','conciliada') */
export const MINISTRACION_ESTADO = {
  PLANIFICADA: 'planificada',
  DEPOSITADA: 'depositada',
  CONCILIADA: 'conciliada',
} as const;
export type MinistracionEstado = (typeof MINISTRACION_ESTADO)[keyof typeof MINISTRACION_ESTADO];

/** historial_pagos_credito.estado — enum('captura_manual','pendiente_autorizacion','autorizado_n2','cierre_n3','pagado','anulado') */
export const HISTORIAL_PAGO_CREDITO_ESTADO = {
  CAPTURA_MANUAL:          'captura_manual',
  PENDIENTE_AUTORIZACION:  'pendiente_autorizacion',
  AUTORIZADO_N2:           'autorizado_n2',
  CIERRE_N3:               'cierre_n3',
  PAGADO:                  'pagado',
  ANULADO:                 'anulado',
} as const;
export type HistorialPagoCreditoEstado = (typeof HISTORIAL_PAGO_CREDITO_ESTADO)[keyof typeof HISTORIAL_PAGO_CREDITO_ESTADO];

/** historial_pagos_credito.tipo_pago — enum('capital','interes','comision') */
export const HISTORIAL_PAGO_CREDITO_TIPO = {
  CAPITAL:  'capital',
  INTERES:  'interes',
  COMISION: 'comision',
} as const;
export type HistorialPagoCreditoTipo = (typeof HISTORIAL_PAGO_CREDITO_TIPO)[keyof typeof HISTORIAL_PAGO_CREDITO_TIPO];

/** Campo computado: vencimiento_prestamo.alerta — retornado por backend, NO columna ENUM de BD */
export const VENCIMIENTO_ALERTA = {
  VENCIDO: 'VENCIDO',
  URGENTE: 'URGENTE',
  PROXIMO: 'PROXIMO',
  OK:      'OK',
} as const;
export type VencimientoAlerta = (typeof VENCIMIENTO_ALERTA)[keyof typeof VENCIMIENTO_ALERTA];

/** distribucion_ganancias.estatus — enum('calculada','aprobada','pagada','cancelada') */
export const DISTRIBUCION_GANANCIA_ESTATUS = {
  CALCULADA: 'calculada',
  APROBADA:  'aprobada',
  PAGADA:    'pagada',
  CANCELADA: 'cancelada',
} as const;
export type DistribucionGananciaEstatus = (typeof DISTRIBUCION_GANANCIA_ESTATUS)[keyof typeof DISTRIBUCION_GANANCIA_ESTATUS];

/** distribuciones_socios.concepto — enum('utilidades','devolucion_capital','prestamo_accionista') (Egresos). Sólo `utilidades` es un REPARTO de utilidades (BUG-4105, D-2026-10-13: el préstamo y la devolución de capital no lo son). */
export const DISTRIBUCION_SOCIOS_CONCEPTO = {
  UTILIDADES: 'utilidades',
  DEVOLUCION_CAPITAL: 'devolucion_capital',
  PRESTAMO_ACCIONISTA: 'prestamo_accionista',
} as const;
export type DistribucionSociosConcepto = (typeof DISTRIBUCION_SOCIOS_CONCEPTO)[keyof typeof DISTRIBUCION_SOCIOS_CONCEPTO];

/** distribuciones_socios.estado — enum('borrador','autorizado','completado') (Egresos). `completado` = el reparto ya se pagó (BUG-4101: ése ya no se cancela). */
export const DISTRIBUCION_SOCIOS_ESTADO = {
  BORRADOR: 'borrador',
  AUTORIZADO: 'autorizado',
  COMPLETADO: 'completado',
} as const;

/** distribucion_ganancias_detalle.estado_pago — enum('pendiente','pagado','rechazado') */
export const DISTRIBUCION_DETALLE_ESTADO_PAGO = {
  PENDIENTE:  'pendiente',
  PAGADO:     'pagado',
  RECHAZADO:  'rechazado',
} as const;
export type DistribucionDetalleEstadoPago = (typeof DISTRIBUCION_DETALLE_ESTADO_PAGO)[keyof typeof DISTRIBUCION_DETALLE_ESTADO_PAGO];

/** credito_refinanciamiento.estatus — enum('propuesta','aprobado','rechazado','ejecutado') */
export const REFINANCIAMIENTO_ESTATUS = {
  PROPUESTA: 'propuesta',
  APROBADO:  'aprobado',
  RECHAZADO: 'rechazado',
  EJECUTADO: 'ejecutado',
} as const;
export type RefinanciamientoEstatus = (typeof REFINANCIAMIENTO_ESTATUS)[keyof typeof REFINANCIAMIENTO_ESTATUS];

/** conciliacion_algoritmo_audit.accion_tomada — enum('auto_conciliado','sugerido_nivel2','sugerido_nivel3','cola_manual','sin_match','manual_confirmado') */
export const CONCILIACION_ACCION = {
  AUTO_CONCILIADO:  'auto_conciliado',
  SUGERIDO_NIVEL2:  'sugerido_nivel2',
  SUGERIDO_NIVEL3:  'sugerido_nivel3',
  COLA_MANUAL:      'cola_manual',
  SIN_MATCH:        'sin_match',
  MANUAL_CONFIRMADO: 'manual_confirmado',
} as const;
export type ConciliacionAccion = (typeof CONCILIACION_ACCION)[keyof typeof CONCILIACION_ACCION];

// ─────────────────────────────────────────────────────────────────────────────
// M06 — CRM VENTAS + PORTALES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * prospectos.etapa — enum('NUEVO','CONTACTADO','CITA_AGENDADA','COTIZACION','APARTADO','KYC','CONTRATO','VENDIDO','PERDIDO')
 * Nota: la columna se llama 'etapa' y los valores son UPPERCASE.
 */
export const PROSPECTO_ESTADO = {
  NUEVO: 'NUEVO',
  CONTACTADO: 'CONTACTADO',
  CITA_AGENDADA: 'CITA_AGENDADA',
  COTIZACION: 'COTIZACION',
  APARTADO: 'APARTADO',
  KYC: 'KYC',
  CONTRATO: 'CONTRATO',
  VENDIDO: 'VENDIDO',
  PERDIDO: 'PERDIDO',
} as const;
export type ProspectoEstado = (typeof PROSPECTO_ESTADO)[keyof typeof PROSPECTO_ESTADO];

/**
 * G02 — el orden del pipeline de prospectos. Sólo se avanza; PERDIDO queda fuera a propósito.
 * Dueño único (BUG-2208): lo usan el rechazo de retroceso del back (crm.service) y el bloqueo
 * de arrastre del Kanban del front. Antes eran dos copias sin nadie que las comparara.
 */
export const ORDEN_PIPELINE_PROSPECTO: readonly ProspectoEstado[] = [
  PROSPECTO_ESTADO.NUEVO,
  PROSPECTO_ESTADO.CONTACTADO,
  PROSPECTO_ESTADO.CITA_AGENDADA,
  PROSPECTO_ESTADO.COTIZACION,
  PROSPECTO_ESTADO.APARTADO,
  PROSPECTO_ESTADO.KYC,
  PROSPECTO_ESTADO.CONTRATO,
  PROSPECTO_ESTADO.VENDIDO,
];

/**
 * BUG-2209 — desde qué etapas se puede APARTAR una unidad (G02). Un solo dueño para el rechazo del
 * back (crm.service, apartar) y el aviso del Kanban del front. Antes eran dos listas a mano.
 */
// BUG-3827 (Mig 121): ESTA MISMA lista es la de las fichas que OCUPAN el correo en `uk_pr_email_proyecto` (`email_uk`, DDL) y en `fichaActivaConElCorreo`.
// Una sola definición de «sin comprometer»; la prueba `varias-unidades-la-llave-del-correo` compara el DDL con ella.
export const ETAPAS_QUE_PUEDEN_APARTAR: readonly ProspectoEstado[] = [
  PROSPECTO_ESTADO.NUEVO,
  PROSPECTO_ESTADO.CONTACTADO,
  PROSPECTO_ESTADO.CITA_AGENDADA,
  PROSPECTO_ESTADO.COTIZACION,
];
/** BUG-3827 r2 — el trato YA lleva una unidad comprometida (apartada, en papeles, en contrato o vendida): lo contrario de arriba, sin los perdidos. */
export const ETAPAS_DE_TRATO_COMPROMETIDO: readonly ProspectoEstado[] = [
  PROSPECTO_ESTADO.APARTADO,
  PROSPECTO_ESTADO.KYC,
  PROSPECTO_ESTADO.CONTRATO,
  PROSPECTO_ESTADO.VENDIDO,
];

/**
 * BUG-2201 — el tope (en MB) de las puertas de archivo que la PANTALLA también revisa antes de
 * subir. Un solo número para el servidor (`puertaDeArchivo({ mb })`) y para el aviso del front:
 * antes Formatos decía «hasta 10 MB» y el servidor aceptaba 20.
 */
export const TOPE_ARCHIVO_MB = {
  DOCUMENTO_PROYECTO: 25,
  PLANTILLA_PROYECTO: 20,
} as const;

/** prospectos.origen_lead — enum('kuommo','broker','referido','web','manual','mapa') */
export const PROSPECTO_ORIGEN = {
  KUOMMO: 'kuommo',
  BROKER: 'broker',
  REFERIDO: 'referido',
  WEB: 'web',
  MANUAL: 'manual',
  MAPA: 'mapa',
} as const;
export type ProspectoOrigen = (typeof PROSPECTO_ORIGEN)[keyof typeof PROSPECTO_ORIGEN];

/** contratos.estado — enum('borrador','enviado_firma','firmado','activo','cancelado','cedido','liquidado') */
export const CONTRATO_ESTADO = {
  BORRADOR: 'borrador',
  ENVIADO_FIRMA: 'enviado_firma',
  FIRMADO: 'firmado',
  ACTIVO: 'activo',
  CANCELADO: 'cancelado',
  CEDIDO: 'cedido',
  LIQUIDADO: 'liquidado',
} as const;
export type ContratoEstado = (typeof CONTRATO_ESTADO)[keyof typeof CONTRATO_ESTADO];

/**
 * contratos.origen_fecha_ancla — enum('cotizacion','firma')
 *
 * De dónde salió la fecha con la que se armó el calendario de pagos (D-14A, Mau
 * 2026-08-14): manda la cotización anexada a la operación, y sólo si no hay de dónde
 * agarrarse se usa la fecha de firma, como hasta el 14-ago-2026. Queda escrito para que
 * nadie tenga que adivinarlo después mirando las cuotas.
 */
export const CONTRATO_ORIGEN_FECHA_ANCLA = {
  COTIZACION:   'cotizacion',
  FIRMA:        'firma',
  REESTRUCTURA: 'reestructura',
} as const;
export type ContratoOrigenFechaAncla = (typeof CONTRATO_ORIGEN_FECHA_ANCLA)[keyof typeof CONTRATO_ORIGEN_FECHA_ANCLA];

/** cotizaciones.estatus — enum('borrador','enviada','aceptada','cancelada','supersedida')
 *  BUG-1747 (I-11): 'vencida' se borró — ver el DDL para la razón completa. */
export const COTIZACION_ESTADO = {
  BORRADOR: 'borrador',
  ENVIADA: 'enviada',
  ACEPTADA: 'aceptada',
  CANCELADA: 'cancelada',
  SUPERSEDIDA: 'supersedida',
} as const;
export type CotizacionEstado = (typeof COTIZACION_ESTADO)[keyof typeof COTIZACION_ESTADO];

/** comisiones_broker.estado — enum('pendiente','devengada','lista_pago','pagada','cancelada') */
export const COMISION_ESTADO = {
  PENDIENTE: 'pendiente',
  DEVENGADA: 'devengada',
  LISTA_PAGO: 'lista_pago',
  PAGADA: 'pagada',
  CANCELADA: 'cancelada',
} as const;
export type ComisionEstado = (typeof COMISION_ESTADO)[keyof typeof COMISION_ESTADO];

/**
 * comisiones_broker.esquema_iva — Mig 56. Los 3 esquemas que el PO elige al cerrar cada venta
 * (D-COMISION-IVA-3-ESQUEMAS, Mau 2026-09-01: "es indiferente, se eligen cuando se cierra la
 * venta — contempla 3 posibilidades").
 * 🔒 El recargo va SOBRE la comisión, jamás sobre el precio de la unidad: el PO lo fijó al
 * decir "6% más un 5%, o sea MENOS que con IVA" — sobre el precio daría 11%, que es más.
 */
export const COMISION_ESQUEMA_IVA = {
  /** Transferencia, el broker factura: +16% sobre la comisión (tasa efectiva 6.96% al 6%). */
  IVA_16: 'iva_16',
  /** Efectivo sin factura, con recargo negociado: +5% sobre la comisión (6.30% al 6%). */
  EFECTIVO_RECARGO_5: 'efectivo_recargo_5',
  /** Efectivo pelón, sin nada encima (6.00% al 6%). */
  EFECTIVO_SIN_RECARGO: 'efectivo_sin_recargo',
} as const;
export type ComisionEsquemaIva = (typeof COMISION_ESQUEMA_IVA)[keyof typeof COMISION_ESQUEMA_IVA];

/** Recargo aplicable a cada esquema, como fracción de la comisión base. */
export const COMISION_RECARGO_POR_ESQUEMA: Record<ComisionEsquemaIva, number> = {
  [COMISION_ESQUEMA_IVA.IVA_16]:               0.16,
  [COMISION_ESQUEMA_IVA.EFECTIVO_RECARGO_5]:   0.05,
  [COMISION_ESQUEMA_IVA.EFECTIVO_SIN_RECARGO]: 0,
};

/**
 * comisiones_broker_tramos.condicion_devengo — Mig 56. Qué hecho hace exigible un tramo.
 * Deroga D-COMISION-BROKER (2026-06-17, "100% al firmar"), derogada por Mau el 2026-09-01.
 */
export const COMISION_TRAMO_CONDICION = {
  AL_FIRMAR:             'al_firmar',
  AL_COMPLETAR_ENGANCHE: 'al_completar_enganche',
  PROPORCIONAL_ENGANCHE: 'proporcional_enganche',
  /**
   * 🔴 EL QUE DE VERDAD USA LA OPERACIÓN (Mig 58, 2026-09-01).
   * El tramo N se devenga cuando se cobra la EXHIBICIÓN N del enganche — no cuando se
   * alcanza un porcentaje.
   * Por qué: `proporcional_enganche` mide proporción, y con los números REALES de Torre Mina
   * eso no dispara. La unidad 116 cobró $145,500 de $296,000 = 49.1554%, y el umbral del
   * primer tramo (la mitad) es 50%: faltaban $2,500 de enganche para soltar $93,300 que el
   * PO da por ganados. Mau lo dijo por exhibiciones, no por porcentaje: "se pagó la mitad del
   * enganche y el enganche se difirió a unos meses después; cuando se pague se paga la otra
   * mitad de la comisión". Contar exhibiciones no depende de redondeos ni de que las
   * parcialidades sean iguales.
   */
  POR_EXHIBICION_ENGANCHE: 'por_exhibicion_enganche',
  A_LA_ESCRITURA:        'a_la_escritura',
  MANUAL:                'manual',
} as const;
export type ComisionTramoCondicion = (typeof COMISION_TRAMO_CONDICION)[keyof typeof COMISION_TRAMO_CONDICION];

/** comisiones_broker_tramos.estado — Mig 56. */
export const COMISION_TRAMO_ESTADO = {
  PENDIENTE: 'pendiente',
  DEVENGADO: 'devengado',
  CANCELADO: 'cancelado',
} as const;
export type ComisionTramoEstado = (typeof COMISION_TRAMO_ESTADO)[keyof typeof COMISION_TRAMO_ESTADO];


/** brokers.estado — enum('activo','inactivo','suspendido') */
export const BROKER_ESTADO = {
  ACTIVO: 'activo',
  INACTIVO: 'inactivo',
  SUSPENDIDO: 'suspendido',
} as const;
export type BrokerEstado = (typeof BROKER_ESTADO)[keyof typeof BROKER_ESTADO];

/** prospectos_documentos.documento_estado — enum('pendiente','validado','rechazado') */
export const KYC_DOCUMENTO_ESTADO = {
  PENDIENTE:  'pendiente',
  VALIDADO:   'validado',
  RECHAZADO:  'rechazado',
} as const;
export type KycDocumentoEstado = (typeof KYC_DOCUMENTO_ESTADO)[keyof typeof KYC_DOCUMENTO_ESTADO];

/** reglas_descuento.tipo_descuento — enum('porcentaje','monto_fijo') */
export const DESCUENTO_TIPO = {
  PORCENTAJE: 'porcentaje',
  MONTO_FIJO: 'monto_fijo',
} as const;
export type DescuentoTipo = (typeof DESCUENTO_TIPO)[keyof typeof DESCUENTO_TIPO];

/** prospectos_interacciones.tipo — enum('llamada','email','whatsapp','cita_agendada','cita_realizada','cotizacion_enviada','cotizacion_vista','apartado_iniciado','documento_cargado','web_simulador','nota_interna') */
export const INTERACCION_TIPO = {
  LLAMADA:            'llamada',
  EMAIL:              'email',
  WHATSAPP:           'whatsapp',
  CITA_AGENDADA:      'cita_agendada',
  CITA_REALIZADA:     'cita_realizada',
  COTIZACION_ENVIADA: 'cotizacion_enviada',
  COTIZACION_VISTA:   'cotizacion_vista',
  APARTADO_INICIADO:  'apartado_iniciado',
  DOCUMENTO_CARGADO:  'documento_cargado',
  WEB_SIMULADOR:      'web_simulador',
  NOTA_INTERNA:       'nota_interna',
} as const;
export type InteraccionTipo = (typeof INTERACCION_TIPO)[keyof typeof INTERACCION_TIPO];

// ─────────────────────────────────────────────────────────────────────────────
// M07 — OBRA
// ─────────────────────────────────────────────────────────────────────────────

/** avance_fisico_partida.estatus — enum('borrador','pendiente_validacion','validado','rechazado') */
export const AVANCE_ESTATUS = {
  BORRADOR:               'borrador',
  PENDIENTE_VALIDACION:   'pendiente_validacion',
  VALIDADO:               'validado',
  RECHAZADO:              'rechazado',
} as const;
export type AvanceEstatus = (typeof AVANCE_ESTATUS)[keyof typeof AVANCE_ESTATUS];

/** presupuesto_parkinson_detector.trayectoria — enum('en_tiempo','atrasado','muy_atrasado','critico') */
export const PARKINSON_TRAYECTORIA = {
  EN_TIEMPO:    'en_tiempo',
  ATRASADO:     'atrasado',
  MUY_ATRASADO: 'muy_atrasado',
  CRITICO:      'critico',
} as const;
export type ParkinsonTrayectoria = (typeof PARKINSON_TRAYECTORIA)[keyof typeof PARKINSON_TRAYECTORIA];

/** pago_avance_control.estado_control — enum('ok','alerta','critico') */
export const PAGO_AVANCE_ESTADO = {
  OK: 'ok',
  ALERTA: 'alerta',
  CRITICO: 'critico',
} as const;
export type PagoAvanceEstado = (typeof PAGO_AVANCE_ESTADO)[keyof typeof PAGO_AVANCE_ESTADO];

/** estimaciones_visualizacion.color_gantt — enum('gris','amarillo','verde','rojo') */
export const ESTIMACION_VISUALIZACION_COLOR = {
  GRIS: 'gris',
  AMARILLO: 'amarillo',
  VERDE: 'verde',
  ROJO: 'rojo',
} as const;
export type EstimacionVisualizacionColor = (typeof ESTIMACION_VISUALIZACION_COLOR)[keyof typeof ESTIMACION_VISUALIZACION_COLOR];

/** obra_contratos.estatus — enum('borrador','pendiente_firma','firmado','vigente','terminado','cancelado') */
export const OBRA_CONTRATO_ESTADO = {
  BORRADOR:        'borrador',
  PENDIENTE_FIRMA: 'pendiente_firma',
  FIRMADO:         'firmado',
  VIGENTE:         'vigente',
  TERMINADO:       'terminado',
  CANCELADO:       'cancelado',
} as const;
export type ObraContratoEstado = (typeof OBRA_CONTRATO_ESTADO)[keyof typeof OBRA_CONTRATO_ESTADO];

// ─────────────────────────────────────────────────────────────────────────────
// M08 — TAREAS + CHAT
// ─────────────────────────────────────────────────────────────────────────────

/** tareas.estado — enum('pendiente','en_proceso','completado','cancelado','archivado') */
export const TAREA_ESTADO = {
  PENDIENTE: 'pendiente',
  EN_PROCESO: 'en_proceso',
  COMPLETADO: 'completado',
  CANCELADO: 'cancelado',
  ARCHIVADO: 'archivado',
} as const;
export type TareaEstado = (typeof TAREA_ESTADO)[keyof typeof TAREA_ESTADO];

/** tareas.prioridad — enum('baja','media','alta','urgente') */
export const TAREA_PRIORIDAD = {
  BAJA: 'baja',
  MEDIA: 'media',
  ALTA: 'alta',
  URGENTE: 'urgente',
} as const;
export type TareaPrioridad = (typeof TAREA_PRIORIDAD)[keyof typeof TAREA_PRIORIDAD];

/** tareas.vinculo_tipo — enum('ticket_garantia','partida_obra','contrato','unidad','credito','proveedor','ninguno') */
export const TAREA_VINCULO_TIPO = {
  TICKET_GARANTIA: 'ticket_garantia',
  PARTIDA_OBRA:    'partida_obra',
  CONTRATO:        'contrato',
  UNIDAD:          'unidad',
  CREDITO:         'credito',
  PROVEEDOR:       'proveedor',
  NINGUNO:         'ninguno',
} as const;
export type TareaVinculoTipo = (typeof TAREA_VINCULO_TIPO)[keyof typeof TAREA_VINCULO_TIPO];

/** chats_canales.tipo — enum('proyecto','tarea','dm','dm_grupo') */
export const CANAL_TIPO = {
  PROYECTO: 'proyecto',
  TAREA: 'tarea',
  DM: 'dm',
  DM_GRUPO: 'dm_grupo',   // Fase 5 M08 — DM multi-usuario (privado real, ni el CEO lo lee)
} as const;
export type CanalTipo = (typeof CANAL_TIPO)[keyof typeof CANAL_TIPO];

/** chats_canales.visibilidad — enum('publico','privado') (Fase 4 M08) */
export const CANAL_VISIBILIDAD = {
  PUBLICO: 'publico',
  PRIVADO: 'privado',
} as const;
export type CanalVisibilidad = (typeof CANAL_VISIBILIDAD)[keyof typeof CANAL_VISIBILIDAD];

/** chats_canal_miembros.rol_canal — enum('admin','miembro') (Fase 4 M08) */
export const CANAL_ROL = {
  ADMIN: 'admin',
  MIEMBRO: 'miembro',
} as const;
export type CanalRol = (typeof CANAL_ROL)[keyof typeof CANAL_ROL];

// ─────────────────────────────────────────────────────────────────────────────
// M09 — POST-VENTA
// ─────────────────────────────────────────────────────────────────────────────

/** tickets_garantia.estatus — enum('REPORTADO','APERTURADO','EN_DIAGNOSTICO','EN_PROCESO','COMPLETADO','CANCELADO') */
export const TICKET_ESTADO = {
  REPORTADO: 'REPORTADO',
  APERTURADO: 'APERTURADO',
  EN_DIAGNOSTICO: 'EN_DIAGNOSTICO',
  EN_PROCESO: 'EN_PROCESO',
  COMPLETADO: 'COMPLETADO',
  CANCELADO: 'CANCELADO',
} as const;
export type TicketEstado = (typeof TICKET_ESTADO)[keyof typeof TICKET_ESTADO];

/** tickets_garantia.urgencia — enum('baja','media','alta','critica') */
export const TICKET_PRIORIDAD = {
  BAJA: 'baja',
  MEDIA: 'media',
  ALTA: 'alta',
  CRITICA: 'critica',
} as const;
export type TicketPrioridad = (typeof TICKET_PRIORIDAD)[keyof typeof TICKET_PRIORIDAD];

/** tickets_garantia.tipo_defecto — enum('humedad_filtracion','instalacion_electrica','instalacion_hidraulica','acabados_pintura','carpinteria_herreria','grietas_estructura','ventanas_puertas','equipamiento_incluido','areas_comunes','otro') */
export const TICKET_CATEGORIA = {
  HUMEDAD_FILTRACION: 'humedad_filtracion',
  INSTALACION_ELECTRICA: 'instalacion_electrica',
  INSTALACION_HIDRAULICA: 'instalacion_hidraulica',
  ACABADOS_PINTURA: 'acabados_pintura',
  CARPINTERIA_HERRERIA: 'carpinteria_herreria',
  GRIETAS_ESTRUCTURA: 'grietas_estructura',
  VENTANAS_PUERTAS: 'ventanas_puertas',
  EQUIPAMIENTO_INCLUIDO: 'equipamiento_incluido',
  AREAS_COMUNES: 'areas_comunes',
  OTRO: 'otro',
} as const;
export type TicketCategoria = (typeof TICKET_CATEGORIA)[keyof typeof TICKET_CATEGORIA];

/** tickets_garantia.origen — enum('portal_cliente','staff_erp','llamada_telefonica','correo_electronico','visita_presencial') */
export const TICKET_ORIGEN = {
  PORTAL_CLIENTE: 'portal_cliente',
  STAFF_ERP: 'staff_erp',
  LLAMADA_TELEFONICA: 'llamada_telefonica',
  CORREO_ELECTRONICO: 'correo_electronico',
  VISITA_PRESENCIAL: 'visita_presencial',
} as const;
export type TicketOrigen = (typeof TICKET_ORIGEN)[keyof typeof TICKET_ORIGEN];

/** actas_entrega.estado — enum('programada','en_proceso','firmada','cancelada') */
export const ACTA_ESTADO = {
  PROGRAMADA: 'programada',
  EN_PROCESO: 'en_proceso',
  FIRMADA: 'firmada',
  CANCELADA: 'cancelada',
} as const;
export type ActaEstado = (typeof ACTA_ESTADO)[keyof typeof ACTA_ESTADO];

/** escrituraciones.estado — enum('pendiente_documentacion','en_tramite','cita_programada','firmada','inscrita_rpp','escritura_entregada','cancelada') */
export const ESCRITURACION_ESTADO = {
  PENDIENTE_DOCUMENTACION: 'pendiente_documentacion',
  EN_TRAMITE: 'en_tramite',
  CITA_PROGRAMADA: 'cita_programada',
  FIRMADA: 'firmada',
  INSCRITA_RPP: 'inscrita_rpp',
  ESCRITURA_ENTREGADA: 'escritura_entregada',
  CANCELADA: 'cancelada',
} as const;
export type EscrituracionEstado = (typeof ESCRITURACION_ESTADO)[keyof typeof ESCRITURACION_ESTADO];

/** tickets_diagnostico.estado — enum('borrador','finalizado') */
export const DIAGNOSTICO_ESTADO = {
  BORRADOR:   'borrador',
  FINALIZADO: 'finalizado',
} as const;
export type DiagnosticoEstado = (typeof DIAGNOSTICO_ESTADO)[keyof typeof DIAGNOSTICO_ESTADO];

/** tickets_asignaciones.estado_asignacion — enum('pendiente','confirmado','en_trabajo','completado','cancelado') */
export const ASIGNACION_TICKET_ESTADO = {
  PENDIENTE:   'pendiente',
  CONFIRMADO:  'confirmado',
  EN_TRABAJO:  'en_trabajo',
  COMPLETADO:  'completado',
  CANCELADO:   'cancelado',
} as const;
export type AsignacionTicketEstado = (typeof ASIGNACION_TICKET_ESTADO)[keyof typeof ASIGNACION_TICKET_ESTADO];

/** tickets_bitacora.tipo_evento — enum('estado_cambiado','nota_interna','nota_cliente','archivo_adjunto','asignacion_cambiada','egreso_vinculado','folio_generado')
 *  Verificado contra la BD real 2026-07-29 (INFORMATION_SCHEMA de erp_test). Los 4 valores
 *  que el sistema genera solo (estado_cambiado, asignacion_cambiada, egreso_vinculado,
 *  folio_generado) no son elegibles por el usuario: el POST de bitácora solo acepta los 3
 *  de nota/adjunto. Aun así el diccionario lleva los 7, porque la LECTURA los recibe todos. */
export const TICKET_BITACORA_TIPO_EVENTO = {
  ESTADO_CAMBIADO:      'estado_cambiado',
  NOTA_INTERNA:         'nota_interna',
  NOTA_CLIENTE:         'nota_cliente',
  ARCHIVO_ADJUNTO:      'archivo_adjunto',
  ASIGNACION_CAMBIADA:  'asignacion_cambiada',
  EGRESO_VINCULADO:     'egreso_vinculado',
  FOLIO_GENERADO:       'folio_generado',
} as const;
export type TicketBitacoraTipoEvento = (typeof TICKET_BITACORA_TIPO_EVENTO)[keyof typeof TICKET_BITACORA_TIPO_EVENTO];

// ─────────────────────────────────────────────────────────────────────────────
// M10 — REPORTES
// ─────────────────────────────────────────────────────────────────────────────

/** Tipo de reporte — concepto de spec, sin ENUM propio en BD */
export const REPORTE_TIPO = {
  FLUJO_CAJA: 'flujo_caja',
  ESTADOS_CUENTA: 'estados_cuenta',
  AVANCE_OBRA: 'avance_obra',
  VENTAS: 'ventas',
  CARTERA: 'cartera',
  RENTABILIDAD: 'rentabilidad',
  PERSONALIZADO: 'personalizado',
} as const;
export type ReporteTipo = (typeof REPORTE_TIPO)[keyof typeof REPORTE_TIPO];

/** reportes_programados.formato — enum('excel','pdf') */
export const REPORTE_FORMATO = {
  PDF: 'pdf',
  EXCEL: 'excel',
} as const;
export type ReporteFormato = (typeof REPORTE_FORMATO)[keyof typeof REPORTE_FORMATO];

/** reportes_programados.frecuencia — enum('diario','semanal','quincenal','mensual') */
export const REPORTE_FRECUENCIA = {
  DIARIO: 'diario',
  SEMANAL: 'semanal',
  QUINCENAL: 'quincenal',
  MENSUAL: 'mensual',
} as const;
export type ReporteFrecuencia = (typeof REPORTE_FRECUENCIA)[keyof typeof REPORTE_FRECUENCIA];

// ─────────────────────────────────────────────────────────────────────────────
// M12 — NOTIFICACIONES
// ─────────────────────────────────────────────────────────────────────────────

export const NOTIFICACION_TIPO = [
  // ───────────────────────────────────────────────────────────────────────────
  // Este catálogo describe lo que el sistema EMITE de verdad, no lo que se
  // deseaba que emitiera. Reconciliado el 2026-08-14 (I-105) contra los 77 puntos
  // que llaman a `crearNotificacion`: antes declaraba 24 tipos mientras el código
  // emitía 62, y **51 de los emitidos no estaban aquí**. La columna
  // `notificaciones.tipo` es VARCHAR(50) sin ENUM: cualquier cadena entra, así que
  // nada cazaba un tipo inventado ni un tipo mal escrito.
  //
  // Los pares que significaban lo mismo con dos nombres se resolvieron a favor del
  // que YA se emite — cambiarle el nombre a algo que ya se manda rompe a quien lo
  // escucha; agregar el que nadie manda no rompe nada. Retirados por eso:
  //   CREDITO_EN_ATRASO     → gana CREDITO_EN_MORA        (crons/cr01.ts:55)
  //   TICKET_ABIERTO        → gana TICKET_CREADO          (postventa.routes.ts:358)
  //   TICKET_RESUELTO       → gana TICKET_COMPLETADO      (postventa.routes.ts:613)
  //   SALDO_BAJO_TESORERIA  → gana SALDO_BAJO_ALERTA      (crons/cr09.ts:66)
  // Ninguno de los cuatro tenía un solo consumidor fuera de este archivo (medido),
  // y los cuatro ganadores son además los que pide la spec G03 — así que esa regla
  // y la de abajo dicen lo mismo aquí.
  //
  // 📜 EN EL NOMBRE MANDA LA SPEC, NO EL CÓDIGO (decisión de Mau, 2026-08-17).
  // Los dos avisos de cobranza de I-105 nacieron con nombre propio y se renombraron
  // a los que G03 ya tenía escritos:
  //   PAGO_RECIBIDO   → PAGO_REGISTRADO      (G03 §M05)
  //   MORA_DETECTADA  → INGRESO_MORA_ALERTA  (G03 §M01, «Cron CR01»)
  // Renombrarlos no rompió a nadie y está MEDIDO, no supuesto: cero filas con los
  // nombres viejos en `erp_rm_desarrollos` y en `erp_test` (las únicas que había
  // vivían en la base de esta pieza y en la del ensayo), cero pruebas de otros
  // carriles que los asevaren, y el emisor nunca salió de esta rama.
  //
  // 🔒 El candado que lo mantiene honesto vive en `__tests__/avisos-cobranza.test.ts`:
  // un tipo emitido sin declarar pone la suite en rojo, y un tipo declarado sin
  // emisor tiene que estar en NOTIFICACION_TIPO_SIN_EMISOR con su razón.
  // ───────────────────────────────────────────────────────────────────────────

  // M01 — Ingresos y cobranza
  'INGRESO_PENDIENTE_VALIDACION',
  'INGRESO_AUTORIZADO',
  'INGRESO_RECHAZADO',
  'PAGO_REGISTRADO',                   // I-105: entró dinero de un cliente contra su plan
  'INGRESO_MORA_ALERTA',               // I-105: una cuota del cliente cayó en mora
  'COBRO_CANCELADO',                   // I-116: se canceló un cobro y la cuota vuelve a deberse
  'APARTADO_ACREDITADO_AL_ENGANCHE',
  'DEVOLUCION_APARTADO_GENERADA',
  'DEVOLUCION_SOBRANTE_GENERADA',       // BUG-2235: se decidió devolver lo que el cliente pagó de más que todo su plan
  'DEVOLUCION_APARTADO_SIN_PAGAR',

  // M02 — Egresos
  'EGRESO_CREADO',
  'EGRESO_PENDIENTE_VALIDACION',
  'EGRESO_PENDIENTE_AUTORIZACION',
  'EGRESO_AUTORIZADO',
  'EGRESO_PAGADO',
  // I-67 (BUG-484, D-2026-09-13-C) — CR02 corre diario y NO se pone al corriente solo:
  // si el recurrente se saltó 1+ periodos, se genera SOLO el del mes en curso y este
  // aviso nombra los periodos saltados (nunca se generan de golpe).
  'EGRESO_RECURRENTE_MESES_SALTADOS',
  // BUG-1172 (I-78 Tarea 4) — CR02 no generó el egreso de este periodo porque excedería el
  // presupuesto de la partida y no hay nadie ahí para autorizar el bypass. Se reintenta al
  // día siguiente (mismo patrón que EGRESO_RECURRENTE_MESES_SALTADOS).
  'EGRESO_RECURRENTE_PRESUPUESTO_EXCEDIDO',
  // BUG-1578 (I-103) — CR02 no generó el egreso de este periodo porque su mes contable
  // ya está cerrado (mismo candado HL-02 que el alta manual, reusado). Se reintenta al
  // día siguiente (mismo patrón que sus dos hermanos de arriba).
  'EGRESO_RECURRENTE_MES_CERRADO',
  // Mig 55 — un abono parcial tambien avisa: el egreso sigue abierto y falta un saldo.
  'EGRESO_ABONO_REGISTRADO',
  'EGRESO_RECHAZADO',
  // I-Pagos-E12 — alguien de campo pide reabrir un mes ya cerrado; dirección (gerente,
  // cfo, ceo) es quien puede reabrirlo (authN3 en `DELETE /cierre-mes/:proyecto_id/…`).
  'CIERRE_MES_REAPERTURA_SOLICITADA',

  // M03 — Bancos y tesorería
  'SALDO_BAJO_ALERTA',
  'CONCILIACION_PARCIAL_REGISTRADA',
  'CONCILIACION_COMPLETADA',
  'MOVIMIENTO_MANUAL_SIN_CONCILIAR',
  'PRESTAMO_VENCIMIENTO_ALERTA',
  'IB2_CAMBIO_PROPUESTO',
  'CAMBIO_CUENTA_PENDIENTE_REVISION',
  'ERROR_INTEGRACION',

  // M04 — Proyectos, obra financiera y mapa
  'PROYECTO_CREADO',
  'PROYECTO_CERRADO',
  'PROYECTO_REABIERTO',
  // BUG-1008 (2026-09-08) — el proyecto nacía sin su caja de efectivo o su partida de
  // Aportación Overhead cuando el sembrado tronaba, y sólo quedaba un logger.warn.
  // proyectos.service.ts:crearProyecto — un catch por cada sembrado, mismo tipo.
  'PROYECTO_SEED_INCOMPLETO',
  'SUPLEMENTO_PENDIENTE',
  'ALERTA_AGRUPADA',
  'ALERTA_SISTEMA',                    // lo emite un TRIGGER de BD, no el código TS
  'MAPA_SVG_SUBIDO',
  'MAPA_SHAPE_ASIGNADO',
  'MAPA_BLOQUEADO',
  'MAPA_DESBLOQUEADO',

  // M05 — Créditos y socios
  'CREDITO_CREADO',
  'CREDITO_EN_MORA',
  'SOCIO_CREADO',
  'MINISTRACION_AUTORIZADA',

  // M06 — CRM, ventas y portales
  'PROSPECTO_CREADO',
  'PROSPECTO_ASIGNADO',
  // BUG-2312 — una ficha activa volvió a pedir cotización desde el mapa público (no se duplica: se avisa).
  'PROSPECTO_VOLVIO_A_COTIZAR',
  // BUG-3827 r2 (Mau, 5-oct 15:09) — una puerta (alta, Kuommo, mapa, broker) abrió una ficha NUEVA para una persona que ya tiene un trato comprometido: se avisa al vendedor del original.
  'PROSPECTO_OTRO_TRATO',
  'COTIZACION_ACEPTADA',
  // Mig 56 — comisiones por etapas (M06 §12.3 las pedía desde la spec; D-COMISION-BROKER
  // las habia dejado sin uso al fijar el pago 100% al firmar. Mau derogo eso el 2026-09-01).
  'COMISION_DEVENGADA',
  'COMISION_LISTA_PAGO',
  // r3-oct (I-205) — una comisión marcada «revisar a mano» por la Mig 99/101 recibió un movimiento
  // y el motor NO la recalculó: se avisa para que alguien la resuelva.
  'COMISION_REVISAR_A_MANO',
  // BUG-2360 — un N3+ resolvió la revisión: la comisión vuelve al motor.
  'COMISION_REVISION_RESUELTA',
  // I-81/T5-d (D-2026-09-14-C) — al reestructurar un contrato, si el nuevo monto deja la
  // comisión viva de la unidad por encima del techo del proyecto, se avisa a N3+ (no se
  // bloquea la reestructura ni se mueve dinero).
  'COMISION_REBASA_TECHO_TRAS_REESTRUCTURA',
  'COTIZACION_POR_EXPIRAR',
  'COTIZACION_EXPIRADA',
  'UNIDAD_APARTADA',
  'APARTADO_EXPIRADO',
  'APARTADO_ESPERA_VALIDACION',
  // I-134/BUG-1877 (D-2026-09-17-C) — el apartado que NADIE mandó a validar (sigue en
  // BORRADOR). Antes CR10 sólo avisaba del que ya está en PENDIENTE_VALIDACION; el
  // capturado-y-olvidado no avisaba a nadie. Es un tipo aparte porque la acción que pide
  // es distinta: aquí no hay nada que "confirmar o rechazar" todavía, hay que MANDARLO a
  // validación primero.
  'APARTADO_SIN_ENVIAR_A_VALIDACION',
  'APARTADO_CON_CONTRATO_VIVO',   // I-117 - vencio pero su unidad tiene contrato vivo: NO se libera
  'CONTRATO_GENERADO',
  'CONTRATO_FIRMADO',
  'CONTRATO_CANCELADO',
  'ALERTA_CRM',

  // M07 — Obra
  'AVANCE_REPORTADO',
  'AVANCE_FISICO_VALIDADO',
  'ESTIMACION_PENDIENTE',
  'PARKINSON_ALERTA',
  'OBRA_CONTROL_ALERTA',
  'DIVERGENCIA_OVERRIDE',

  // M08 — Tareas y chat
  'TAREA_ASIGNADA',
  'TAREA_COMPLETADA',
  'TAREA_VENCIDA',
  'TAREA_MENCION',
  'CHAT_MENCION',

  // M09 — Post-venta y escrituración
  'TICKET_CREADO',
  'TICKET_APERTURADO',
  'TICKET_EN_PROCESO',
  'TICKET_ASIGNACION_CREADA',
  'TICKET_LISTO_PARA_CIERRE',
  'TICKET_COMPLETADO',
  'ACTA_PROGRAMADA',
  'ACTA_FIRMADA',
  'ESCRITURACION_CREADA',
  'ESCRITURACION_INSCRITA',
  'ESCRITURACION_CANCELADA',
  'ESCRITURA_ENTREGADA',

  // M14 — Overhead corporativo
  'OVERHEAD_APORTES_GENERADOS',
  'OVERHEAD_REPARTO_DESBALANCEADO',   // D-14C/D4: las reglas activas no suman 100% — no se generó nada ese mes
  'OVERHEAD_GASTO_INVALIDO', // gastoReal salió negativo (dato corrupto) — revisión adversarial 25-ago, paso obligatorio
  'OVERHEAD_MES_CERRADO_SIN_REPARTIR', // BUG-4056: CR15 saltó gastos de la oficina de un mes ya cerrado en un proyecto del reparto (no los reparte solo)
  // BUG-280 — la venta se cerró y NO se le pudo poner comisión (falta la comisión sugerida del
  // proyecto, o la de esta venta). Tiene nombre PROPIO a propósito: usar COMISION_DEVENGADA para
  // avisar justo lo contrario hacía que el tablero contara como devengada una comisión inexistente.
  'VENTA_SIN_COMISION',
  // BUG-2213 — el reporte programado se generó pero su correo NO salió; se le dice al dueño del
  // reporte (el cron lo reintenta cada hora sin regenerar el archivo).
  'REPORTE_CORREO_NO_SALIO',
  'REPORTE_CORREO_SE_DEJO_DE_REINTENTAR',
] as const;
export type NotificacionTipo = (typeof NOTIFICACION_TIPO)[number];

/**
 * I-253 (BUG-2852) — `notificaciones.modulo`: el catálogo de las pantallas/áreas que emiten avisos.
 * La columna es VARCHAR(30) sin ENUM de MySQL (el front y el cron de archivo la leen como texto), así
 * que nada impedía guardar `ingreso` en un aviso y `ingresos` en otro (20 valores medidos, con los
 * pares ingreso/ingresos, egreso/egresos y banco/bancos). Ahora `crearNotificacion` normaliza al
 * escribir y rechaza un módulo fuera de esta lista. Medido: 18 módulos emitidos hoy.
 */
export const NOTIFICACION_MODULO = [
  'auth', 'bancos', 'chats', 'configuracion', 'contabilidad', 'contactos', 'creditos', 'crm',
  'egresos', 'ingresos', 'nomina', 'obra', 'overhead', 'postventa', 'proyectos', 'reportes',
  'tareas', 'unidades',
] as const;
export type NotificacionModulo = (typeof NOTIFICACION_MODULO)[number];

/**
 * Cómo se escribían ANTES los módulos que hoy tienen su nombre en plural: los avisos viejos no se
 * reescriben (Mig 0), se leen con los dos nombres. Clave = nombre del catálogo; valor = sinónimos viejos.
 */
export const SINONIMOS_DE_MODULO: Record<string, readonly string[]> = {
  ingresos: ['ingreso'],
  egresos:  ['egreso'],
  bancos:   ['banco'],
};

/**
 * Los tipos que el catálogo PROMETE y que hoy NINGÚN punto del código TS emite.
 *
 * No es una lista de permitidos para esconder deuda: es lo contrario. Sin ella, un
 * tipo declarado sin emisor es una promesa muda que nadie ve; aquí queda escrito
 * QUÉ falta y POR QUÉ, y la prueba obliga a que cada nuevo tipo declarado traiga su
 * emisor o su renglón con razón. Si mañana alguien lo implementa, tiene que sacarlo
 * de aquí — y si no lo saca, la prueba se lo dice.
 */
export const NOTIFICACION_TIPO_SIN_EMISOR: Record<string, string> = {
  // Lo emite un TRIGGER de BD (trg_alerta_divergencia_gantt, DDL_v14_MASTER.sql:5096),
  // no el código TypeScript. El único aviso del sistema que no pasa por el helper.
  ALERTA_SISTEMA: 'lo emite un trigger de BD, no el código TS',

  // Flujos de rechazo: la ruta responde el error al usuario pero no avisa a nadie más.
  // Fuera del contrato de I-105 (que es pago y mora) — queda listado, no construido.
  INGRESO_RECHAZADO: 'no hay punto de emisión: rechazar un ingreso no avisa a nadie',
  EGRESO_RECHAZADO:  'no hay punto de emisión: rechazar un egreso no avisa a nadie',

  // Prometidos por G03 y nunca construidos. Ninguno es de cobranza.
  CONCILIACION_COMPLETADA:  'sólo se emite la parcial (CONCILIACION_PARCIAL_REGISTRADA)',
  COTIZACION_EXPIRADA:      'sólo se avisa la que está POR expirar (cr14), no la ya expirada',
  MINISTRACION_AUTORIZADA:  'la ministración de crédito no avisa',
  MAPA_SVG_SUBIDO:          'existe como evento de auditoría, no como aviso',
  MAPA_SHAPE_ASIGNADO:      'existe como evento de auditoría, no como aviso',

  // 🔴 D-2026-09-05-B — Mau quitó la expiración automática de apartados: «únicamente manual
  // se le quita el apartado». CR10 emitía este aviso justo después de liberar la unidad; al
  // irse la liberación, se fue su emisor. NO se borra el tipo, porque el aviso describe algo
  // que puede volver a existir el día que alguien construya un «apartado vencido» que no
  // libere nada. Queda declarado y a la vista, que es lo que este registro existe para hacer.
  APARTADO_EXPIRADO: 'D-2026-09-05-B: ya no hay expiración automática, así que nadie lo emite',
};

/**
 * Los tres avisos de la cadena de venta, como constantes con nombre (§25 REGLA 1:
 * nada de strings de ENUM sueltos en el código).
 *
 * El tipo `Record<..., NotificacionTipo>` es el candado: si alguien borra uno de
 * estos valores del array de arriba, el typecheck truena aquí en vez de dejar un
 * aviso que nadie puede leer.
 */
export const NOTIFICACION_TIPO_VENTA: Record<
  'COTIZACION_ACEPTADA' | 'UNIDAD_APARTADA' | 'CONTRATO_GENERADO',
  NotificacionTipo
> = {
  COTIZACION_ACEPTADA: 'COTIZACION_ACEPTADA',
  UNIDAD_APARTADA:     'UNIDAD_APARTADA',
  CONTRATO_GENERADO:   'CONTRATO_GENERADO',
} as const;

/**
 * Los dos avisos que cobranza necesita para hacer su trabajo sin ir a mirar (I-105).
 * Mismo candado que los de venta: si alguien borra el valor del array de arriba, el
 * typecheck truena aquí en vez de dejar un aviso que nadie puede leer.
 *
 * 📜 Los nombres son los de la spec G03 —`PAGO_REGISTRADO` (§M05) e
 * `INGRESO_MORA_ALERTA` (§M01, «Cron CR01»)— por decisión de Mau del 2026-08-17:
 * donde la spec y el código se llamen distinto, gana la spec.
 */
export const NOTIFICACION_TIPO_COBRANZA: Record<
  'PAGO_REGISTRADO' | 'INGRESO_MORA_ALERTA' | 'COBRO_CANCELADO',
  NotificacionTipo
> = {
  PAGO_REGISTRADO:     'PAGO_REGISTRADO',
  INGRESO_MORA_ALERTA: 'INGRESO_MORA_ALERTA',
  // I-116: cancelar un cobro ya aplicado dejaba la cuota debiéndose en silencio, y el
  // `PAGO_REGISTRADO` viejo se quedaba en la campana afirmando que estaba saldada.
  COBRO_CANCELADO:     'COBRO_CANCELADO',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// M14 — OVERHEAD CORPORATIVO
// ─────────────────────────────────────────────────────────────────────────────

/** reglas_overhead.tipo_regla — enum('porcentaje_ingreso','porcentaje_egreso','monto_fijo_mensual') */
// D-14C/D4 (2026-08-14, confirmado 2026-08-25): única fórmula — % del gasto real de
// oficina del mes, las reglas activas deben sumar 100%. Derogados porcentaje_ingreso/
// porcentaje_egreso/monto_fijo_mensual — ninguno implementaba la regla real.
export const OVERHEAD_REGLA_TIPO = {
  PORCENTAJE_OVERHEAD: 'porcentaje_overhead',
} as const;
export type OverheadReglaTipo = (typeof OVERHEAD_REGLA_TIPO)[keyof typeof OVERHEAD_REGLA_TIPO];

/** aportaciones_overhead.estatus — enum('propuesta','pendiente','en_proceso','ejecutado','cancelado') */
export const APORTACION_OVERHEAD_ESTATUS = {
  PROPUESTA:  'propuesta',
  PENDIENTE:  'pendiente',
  EN_PROCESO: 'en_proceso',
  EJECUTADO:  'ejecutado',
  CANCELADO:  'cancelado',
} as const;
export type AportacionOverheadEstatus = (typeof APORTACION_OVERHEAD_ESTATUS)[keyof typeof APORTACION_OVERHEAD_ESTATUS];

/** Estado de un gasto de la oficina en `GET /overhead/gastos` (D-2026-10-08-K) — NO es columna: lo calcula `desgloseDeGastos` de sus aportaciones. */
export const OVERHEAD_GASTO_ESTADO = {
  POR_REPARTIR: 'por_repartir',
  REPARTIDO:    'repartido',
  PAGADO:       'pagado',
  CANCELADO:    'cancelado',
} as const;
export type OverheadGastoEstado = (typeof OVERHEAD_GASTO_ESTADO)[keyof typeof OVERHEAD_GASTO_ESTADO];

/** overhead_gastos.periodicidad — enum('quincenal','mensual') (BUG-3828: la nómina es quincenal, la renta mensual) */
export const OVERHEAD_GASTO_PERIODICIDAD = {
  QUINCENAL: 'quincenal',
  MENSUAL:   'mensual',
} as const;
export type OverheadGastoPeriodicidad = (typeof OVERHEAD_GASTO_PERIODICIDAD)[keyof typeof OVERHEAD_GASTO_PERIODICIDAD];

// ─────────────────────────────────────────────────────────────────────────────
// M13 — CONFIGURACIÓN
// ─────────────────────────────────────────────────────────────────────────────

export const INTEGRACION_SERVICIO = {
  RESEND: 'resend',
  MIFIEL: 'mifiel',
  GOOGLE_VISION: 'google_vision',
  KUOMMO: 'kuommo',
} as const;
export type IntegracionServicio = (typeof INTEGRACION_SERVICIO)[keyof typeof INTEGRACION_SERVICIO];

/** integraciones_config.resultado_prueba — enum('exitoso','fallido','pendiente') */
export const INTEGRACION_RESULTADO = {
  EXITOSO:  'exitoso',
  FALLIDO:  'fallido',
  PENDIENTE: 'pendiente',
} as const;
export type IntegracionResultado = (typeof INTEGRACION_RESULTADO)[keyof typeof INTEGRACION_RESULTADO];

/** parametros_globales.tipo — enum('decimal','integer','boolean','string') */
export const PARAMETRO_TIPO = {
  DECIMAL:  'decimal',
  INTEGER:  'integer',
  BOOLEAN:  'boolean',
  STRING:   'string',
} as const;
export type ParametroTipo = (typeof PARAMETRO_TIPO)[keyof typeof PARAMETRO_TIPO];

/** parametros_globales.grupo — enum('mora','varianza','alertas','rentabilidad','general','fiscal') */
export const PARAMETRO_GRUPO = {
  MORA:           'mora',
  VARIANZA:       'varianza',
  ALERTAS:        'alertas',
  RENTABILIDAD:   'rentabilidad',
  GENERAL:        'general',
  FISCAL:         'fiscal',
} as const;
export type ParametroGrupo = (typeof PARAMETRO_GRUPO)[keyof typeof PARAMETRO_GRUPO];

// ─────────────────────────────────────────────────────────────────────────────
// M15 — CONTABILIDAD
// ─────────────────────────────────────────────────────────────────────────────

/** polizas.estatus — enum('borrador','cerrada','cancelada') */
export const POLIZA_ESTATUS = {
  BORRADOR:  'borrador',
  CERRADA:   'cerrada',
  CANCELADA: 'cancelada',
} as const;
export type PolizaEstatus = (typeof POLIZA_ESTATUS)[keyof typeof POLIZA_ESTATUS];

// ─────────────────────────────────────────────────────────────────────────────
// AUDITORÍA — global
// ─────────────────────────────────────────────────────────────────────────────

export const AUDITORIA_EVENTO = [
  // Ingresos
  'INGRESO_CREADO', 'INGRESO_VALIDADO', 'INGRESO_RECHAZADO', 'INGRESO_CONCILIADO',
  // Mora (M01/M05) — las 4 acciones de `POST /ingresos/intereses-moratorios/resolver`.
  // 'MORA_DIFERIDA_LIQUIDACION' es la ficha de negociación de Mau (decisión 2026-08-14,
  // reafirmada 2026-09-04): la mora no se cobra mes con mes, se deja para la liquidación.
  // Los otros dos ya se usaban en el código desde antes y NO estaban en este catálogo.
  'COBRO_INTERES_MORATORIO', 'CONDONACION_INTERES', 'MORA_DIFERIDA_LIQUIDACION',
  // Egresos
  'EGRESO_CREADO', 'EGRESO_AUTORIZADO', 'EGRESO_RECHAZADO', 'EGRESO_PAGADO', 'EGRESO_CANCELADO',
  'EGRESO_ABONO_REGISTRADO',
  // D-2026-09-04 — deshacer el último paso de la cadena (validar/autorizar/enviar/rechazar).
  // Un solo evento para los cuatro: cuál se deshizo va en `valor_nuevo.paso_deshecho`.
  'EGRESO_PASO_DESHECHO',
  // Créditos
  'CREDITO_CREADO', 'CREDITO_AUTORIZADO', 'CREDITO_VIGENTE', 'CREDITO_LIQUIDADO',
  // CRM
  'PROSPECTO_CREADO', 'PROSPECTO_ACTUALIZADO', 'CONTRATO_FIRMADO', 'CONTRATO_GENERADO',
  'CONTRATO_ENVIADO_FIRMA', 'UNIDAD_APARTADA', 'UNIDAD_VENDIDA', 'APARTADO_EXPIRADO',
  'INTERACCION_REGISTRADA', 'BROKER_REGISTRADO', 'BROKER_PORTAL_ACTIVADO',
  'COMISION_BROKER_PAGADA', 'COMISION_BROKER_REGISTRADA', 'COMISION_TRAMO_DEVENGADO',
  'COMPROBANTE_SUBIDO', 'COMPROBANTE_CONCILIADO',
  'COTIZACION_CREADA', 'COTIZACION_ENVIADA', 'COTIZACION_ACEPTADA', 'COTIZACION_CANCELADA',
  // Mapa
  'MAPA_SVG_SUBIDO', 'MAPA_SHAPE_ASIGNADO', 'MAPA_BLOQUEADO', 'MAPA_DESBLOQUEADO',
  // Obra (M07)
  'PARTIDA_DEPENDENCIA_CREADA',
  'PARTIDA_DEPENDENCIA_ELIMINADA',
  // Post-venta
  'TICKET_ABIERTO', 'TICKET_RESUELTO', 'ACTA_FIRMADA', 'ESCRITURACION_COMPLETADA',
  // Sistema
  'USUARIO_CREADO', 'USUARIO_DESACTIVADO', 'PERIODO_CERRADO', 'PERIODO_REABIERTO',
  'BYPASS_AUTORIZADO',
] as const;
export type AuditoriaEvento = (typeof AUDITORIA_EVENTO)[number];

// ─────────────────────────────────────────────────────────────────────────────
// CÓDIGOS DE ERROR HTTP — envelope estándar
// ─────────────────────────────────────────────────────────────────────────────

export const ERROR_CODES = {
  // Auth
  TOKEN_REQUERIDO: 'TOKEN_REQUERIDO',
  TOKEN_INVALIDO: 'TOKEN_INVALIDO',
  TOKEN_EXPIRADO: 'TOKEN_EXPIRADO',
  TENANT_REQUERIDO: 'TENANT_REQUERIDO',
  TENANT_INVALIDO: 'TENANT_INVALIDO',
  // Permisos
  NIVEL_INSUFICIENTE: 'NIVEL_INSUFICIENTE',
  ANTI_AUTOAUTH: 'ANTI_AUTOAUTH',
  PROYECTO_NO_ASIGNADO: 'PROYECTO_NO_ASIGNADO',
  MAPA_BLOQUEADO: 'MAPA_BLOQUEADO',
  // Recursos
  NO_ENCONTRADO: 'NO_ENCONTRADO',
  CAMPO_REQUERIDO: 'CAMPO_REQUERIDO',
  // Estado/negocio
  ESTADO_INVALIDO: 'ESTADO_INVALIDO',
  DUPLICADO: 'DUPLICADO',
  REGLA_NEGOCIO: 'REGLA_NEGOCIO',
  NOTA_BYPASS_MUY_CORTA: 'NOTA_BYPASS_MUY_CORTA',
  // I-187 (BUG-2084) — NOTA_BYPASS_MUY_CORTA significa "tu nota para saltarte un límite de
  // autorización es corta" (HL-04/HL-10). No es lo mismo que "te faltó escribir un texto
  // obligatorio" (postventa: resolución/cancelación de ticket, mínimo 5, sin relación con
  // ningún tope de dinero). Separado para que un consumidor (pantalla, otro sistema) pueda
  // distinguir los dos casos — no se unifican los mínimos, sólo el código.
  TEXTO_REQUERIDO_MUY_CORTO: 'TEXTO_REQUERIDO_MUY_CORTO',
  PERIODO_CERRADO: 'PERIODO_CERRADO',
  // Infraestructura
  RATE_LIMIT: 'RATE_LIMIT',
  SERVICIO_NO_DISPONIBLE: 'SERVICIO_NO_DISPONIBLE',
  INTEGRACION_NO_CONFIGURADA: 'INTEGRACION_NO_CONFIGURADA',
  // Obra (M07) — Cronograma
  DEPENDENCIA_CIRCULAR:  'DEPENDENCIA_CIRCULAR',
  PARTIDA_INVALIDA:      'PARTIDA_INVALIDA',
  // Comisiones (I-81) — una comisión viva por unidad, con techo
  COMISION_UNIDAD_YA_VIVA: 'COMISION_UNIDAD_YA_VIVA',
  COMISION_REBASA_TECHO:   'COMISION_REBASA_TECHO',
} as const;
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

// ─────────────────────────────────────────────────────────────────────────────
// API ENVELOPES — respuestas estándar del backend
// ─────────────────────────────────────────────────────────────────────────────

/** Respuesta exitosa de colección con paginación */
export interface ApiListResponse<T> {
  data: T[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

/** Respuesta exitosa de recurso individual */
export interface ApiItemResponse<T> {
  data: T;
  message?: string;
}

/** Respuesta de mutación sin recurso de retorno */
export interface ApiMessageResponse {
  message: string;
}

/** Respuesta de error — SIEMPRE este envelope */
export interface ApiErrorResponse {
  error: true;
  code: ErrorCode;
  message: string;
  field?: string;
  details?: Record<string, unknown>;
}

// ─────────────────────────────────────────────────────────────────────────────
// CRON JOBS — IDs y horarios (fuente: G05)
// ─────────────────────────────────────────────────────────────────────────────

export const CRON_JOBS = {
  CR01: { id: 'CR01', descripcion: 'Calcular interés moratorio',            horario: '0 2 * * *',     modulo: 'M01/M05' },
  // I-88 T1 — horario corregido a DIARIO (D-2026-09-13-C): scheduler.ts y registry.ts ya lo
  // tenían bien; sólo CRON_JOBS se había quedado con el mensual viejo. T2 hace que registry
  // LEA este horario — si no se corrige aquí, T2 regresa CR02 a mensual.
  CR02: { id: 'CR02', descripcion: 'Generar egresos recurrentes (diario, sin ponerse al corriente solo — D-2026-09-13-C)', horario: '0 8 * * *', modulo: 'M02' },
  CR03: { id: 'CR03', descripcion: 'Detector Ley de Parkinson',             horario: '0 7 * * 1-5',  modulo: 'M07' },
  CR04: { id: 'CR04', descripcion: 'Digesto de notificaciones',             horario: '0 9 * * *',     modulo: 'M12' },
  CR05: { id: 'CR05', descripcion: 'Alertas tareas vencidas',               horario: '0 8 * * *',     modulo: 'M08' },
  // I-88 T1 — 'variable' no es un cron válido; scheduler.ts corre cada hora y cada reporte
  // trae su propia próxima ejecución. Corregido para que T2 (registry lee de aquí) no herede algo inválido.
  CR06: { id: 'CR06', descripcion: 'Reportes programados (cada hora; cada reporte trae su propia próxima ejecución)', horario: '0 * * * *', modulo: 'M10' },
  CR07: { id: 'CR07', descripcion: 'Limpieza archivos huérfanos',           horario: '0 3 * * 0',     modulo: 'M08' },
  CR08: { id: 'CR08', descripcion: 'Archivar notificaciones >90d',          horario: '0 4 1 * *',     modulo: 'M12' },
  CR09: { id: 'CR09', descripcion: 'Alerta saldo bajo tesorería',           horario: '0 8 * * 1-5',  modulo: 'M03' },
  // I-88 T1 — descripción corregida: D-2026-09-05-B quitó la expiración automática ("únicamente
  // manual se le quita el apartado"). El horario (*/30 * * * *) ya estaba bien.
  CR10: { id: 'CR10', descripcion: 'Vigilancia de apartados — sólo avisa, NO expira (D-2026-09-05-B)', horario: '*/30 * * * *', modulo: 'M06' },
  CR11: { id: 'CR11', descripcion: 'Recordatorio cambios pendientes IB2',  horario: '0 9 * * 1-5',  modulo: 'M03' },
  CR12: { id: 'CR12', descripcion: 'Score Decay prospectos CRM',            horario: '0 3 * * *',     modulo: 'M06' },
  CR13: { id: 'CR13', descripcion: 'Alertas proactivas CRM',                horario: '0 8 * * 1-5',  modulo: 'M06' },
  CR14: { id: 'CR14', descripcion: 'Cotizaciones por expirar',              horario: '0 9 * * *',     modulo: 'M06' },
  CR15: { id: 'CR15', descripcion: 'Reparto diario de gastos de oficina (lo que ya cerró)',  horario: '0 7 * * *',     modulo: 'M14' },
  'CR-M03-A': { id: 'CR-M03-A', descripcion: 'Alertas de vencimiento de préstamos',       horario: '0 8 * * *',    modulo: 'M03' },
  'CR-M03-B': { id: 'CR-M03-B', descripcion: 'Movimientos manuales >7 días sin conciliar', horario: '0 9 * * *',    modulo: 'M03' },
  'CR-M03-C': { id: 'CR-M03-C', descripcion: 'Actualiza tipo de cambio (Banxico)',         horario: '30 10 * * *',  modulo: 'M03' },
  // I-88 T1 — CRM04 corregido a 1×/día 7 AM (D-2026-09-07-J: "cada 2 horas es una tontería").
  // Antes decía '0 */2 * * *' — sólo en ESTE archivo (shared); backend ya traía el valor correcto.
  CRM04: { id: 'CRM04', descripcion: 'Recálculo rentabilidad M04',         horario: '0 7 * * *',  modulo: 'M04' },
} as const;
export type CronJobId = keyof typeof CRON_JOBS;

// ─────────────────────────────────────────────────────────────────────────────
// MÓDULOS — IDs y nombres canónicos
// ─────────────────────────────────────────────────────────────────────────────

export const MODULOS = {
  M00: 'sistema',
  M01: 'ingresos',
  M02: 'egresos',
  M03: 'bancos',
  M04: 'proyectos',
  M05: 'creditos',
  M06: 'crm',
  M07: 'obra',
  M08: 'tareas',
  M09: 'postventa',
  M10: 'reportes',
  M11: 'dashboard',
  M12: 'notificaciones',
  M13: 'configuracion',
  M14: 'overhead',
} as const;
export type ModuloId = keyof typeof MODULOS;
export type ModuloNombre = (typeof MODULOS)[ModuloId];

// ─────────────────────────────────────────────────────────────────────────────
// TABLA DE ENUMs — índice por tabla.columna para los tests de consistencia
// Fuente de verdad: INFORMATION_SCHEMA.COLUMNS de erp_test (validado 2026-05-01)
// Sólo incluye columnas que existen como ENUM en la BD real.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Mapa canónico de ENUMs por tabla.columna.
 * Usado en dictionary-consistency.test.ts para comparar contra INFORMATION_SCHEMA.
 * Claves: 'tabla.columna_exacta_en_bd' — NO nombres de conceptos de spec.
 */
export const ENUM_MAP: Record<string, readonly string[]> = {
  // M00 — Sistema
  'usuarios.rol':                             Object.values(USUARIO_ROL),
  'notificaciones.modulo':                    NOTIFICACION_MODULO,
  'proyectos.estado':                         Object.values(PROYECTO_ESTADO),
  'socios.tipo_participacion':                Object.values(SOCIO_TIPO_PARTICIPACION),
  'ingresos_registros.moneda':                Object.values(MONEDA),
  // M01 — Ingresos
  'ingresos_registros.tipo_ingreso':          Object.values(INGRESO_TIPO),
  'ingresos_registros.estatus':               Object.values(INGRESO_ESTATUS),
  'ingresos_registros.origen_ingreso':        Object.values(ORIGEN_INGRESO),
  'ingresos_registros.motivo_cancelacion':    Object.values(INGRESO_MOTIVO_CANCELACION),
  'recibos.tipo':                             Object.values(RECIBO_TIPO),
  'planes_pago.tipo_pago':                    Object.values(PLAN_PAGO_TIPO),
  'planes_pago.estado':                       Object.values(PLAN_PAGO_ESTADO),
  'cobranza_registros.estado':                Object.values(COBRANZA_ESTATUS),
  'pagos_comprobantes.estado':                Object.values(COMPROBANTE_ESTADO),
  'pagos_comprobantes.origen_carga':           Object.values(COMPROBANTE_ORIGEN),
  // M02 — Egresos
  'cuentas_contables.tipo':                   Object.values(CUENTA_CONTABLE_TIPO),
  'egresos_registros.tipo_egreso':            Object.values(EGRESO_TIPO),
  // BUG-226: esta columna NO estaba en el mapa, y por eso el gate nunca la comparó
  // contra la BD — el DDL le tenía 11 valores mientras la migrada tenía 12.
  'egresos_recurrentes.tipo_egreso':          Object.values(EGRESO_TIPO),
  'egresos_registros.tipo_deuda':             Object.values(EGRESO_TIPO_DEUDA),
  'egresos_registros.estatus':                Object.values(EGRESO_ESTATUS),
  'egresos_registros.origen_egreso':          Object.values(ORIGEN_EGRESO),
  'egresos_registros.recurrencia_frecuencia': Object.values(EGRESO_RECURRENCIA),
  // M03 — Bancos
  'cuentas_banco.estado':                     Object.values(CUENTA_BANCO_ESTADO),
  'cuentas_banco.proposito_cuenta':           Object.values(CUENTA_PROPOSITO),
  'ingresos_registros.metodo_pago':           Object.values(METODO_PAGO_INGRESO),
  'egresos_registros.metodo_pago':            Object.values(METODO_PAGO),
  'catalogo_presupuesto.metodo_pago':         Object.values(METODO_PAGO),
  'cuentas_banco.tipo_cuenta':                Object.values(CUENTA_TIPO),
  'movimientos_bancarios.tipo_movimiento':    Object.values(MOVIMIENTO_TIPO),
  'movimientos_bancarios.origen':             Object.values(MOVIMIENTO_ORIGEN),
  'movimientos_bancarios.estado_conciliacion': Object.values(MOVIMIENTO_ESTATUS),
  // I-Pagos-E-r1/E-r2 revisión adversarial 2ª ronda (R2-9/§25 REGLA 4) — faltaba en el mapa;
  // la columna real SÍ trae 'transferencia' (DDL `:2808`), por eso TRANSACCION_TIPO la incluye.
  'movimientos_bancarios.tipo_transaccion_erp': Object.values(TRANSACCION_TIPO),
  'conciliacion_bancaria.estatus':            Object.values(CONCILIACION_ESTATUS),
  'cuentas_bancarias_cambios_pendientes.estado':             Object.values(IB2_CAMBIO_ESTADO),
  'comisiones_broker_cambios_pendientes.estado':             Object.values(IB2_CAMBIO_ESTADO),   // I-262: misma forma que IB2
  'conciliacion_parcial.estado':              Object.values(CONCILIACION_PARCIAL_ESTADO),
  'conciliacion_transacciones_erp.tipo_transaccion_erp': Object.values(TRANSACCION_TIPO),
  // I-Pagos-E-r7 — ver el comentario de CFDI_METODO_PAGO arriba: no es columna de BD.
  // dictionary-consistency.test.ts la trae en CONCEPTOS_SIN_COLUMNA con su razón.
  'cfdi_xml.metodo_pago':                     Object.values(CFDI_METODO_PAGO),
  'transferencia_interna_prestamo.estatus':    Object.values(TRANSFERENCIA_INTERNA_ESTATUS),
  // M04 — Proyectos
  'proyectos.tipo_proyecto':                  Object.values(PROYECTO_TIPO),
  'unidades.estado_venta':                    Object.values(UNIDAD_ESTADO_VENTA),
  'unidades.tipo':                            Object.values(UNIDAD_TIPO),
  'estimaciones_proyecto.estatus':            Object.values(ESTIMACION_ESTADO),
  'suplementos_presupuesto.estado':           Object.values(SUPLEMENTO_ESTADO),
  'alertas_proyecto.estado':                  Object.values(ALERTA_PROYECTO_ESTADO),
  'alertas_proyecto.tipo':                    Object.values(ALERTA_PROYECTO_TIPO),
  'alertas_proyecto.prioridad':               Object.values(ALERTA_PROYECTO_PRIORIDAD),
  // M05 — Créditos
  'creditos.tipo_credito':                    Object.values(CREDITO_TIPO),
  'creditos.tipo_linea':                      Object.values(CREDITO_LINEA),
  'creditos.estatus':                         Object.values(CREDITO_ESTADO),
  'aportaciones_socios.estatus':              Object.values(APORTACION_ESTADO),
  'ministracion_credito.estatus':             Object.values(MINISTRACION_ESTADO),
  'historial_pagos_credito.estado':           Object.values(HISTORIAL_PAGO_CREDITO_ESTADO),
  'historial_pagos_credito.tipo_pago':        Object.values(HISTORIAL_PAGO_CREDITO_TIPO),
  'distribucion_ganancias.estatus':             Object.values(DISTRIBUCION_GANANCIA_ESTATUS),
  'distribuciones_socios.concepto':             Object.values(DISTRIBUCION_SOCIOS_CONCEPTO),
  'distribuciones_socios.estado':               Object.values(DISTRIBUCION_SOCIOS_ESTADO),
  'distribucion_ganancias_detalle.estado_pago': Object.values(DISTRIBUCION_DETALLE_ESTADO_PAGO),
  'credito_refinanciamiento.estatus':           Object.values(REFINANCIAMIENTO_ESTATUS),
  'conciliacion_algoritmo_audit.accion_tomada': Object.values(CONCILIACION_ACCION),
  // M06 — CRM
  'prospectos.etapa':                         Object.values(PROSPECTO_ESTADO),
  'prospectos.origen_lead':                   Object.values(PROSPECTO_ORIGEN),
  'contratos.estado':                         Object.values(CONTRATO_ESTADO),
  'contratos.origen_fecha_ancla':             Object.values(CONTRATO_ORIGEN_FECHA_ANCLA),
  // Mig 54 — revisión adversarial (31-ago, hallazgo I-3): quedaban fuera del guard de drift.
  'contratos.enganche_metodo_pago':           Object.values(METODO_PAGO_INGRESO),
  'contratos.liquidacion_metodo_pago':        Object.values(METODO_PAGO_INGRESO),
  'cotizaciones.estatus':                     Object.values(COTIZACION_ESTADO),
  'comisiones_broker.estado':                 Object.values(COMISION_ESTADO),
  'comisiones_broker.esquema_iva':            Object.values(COMISION_ESQUEMA_IVA),
  'comisiones_broker_tramos.esquema_iva':     Object.values(COMISION_ESQUEMA_IVA), // Mig 101 (D-2026-09-23-M)
  'comisiones_broker_tramos.condicion_devengo': Object.values(COMISION_TRAMO_CONDICION),
  'comisiones_broker_tramos.estado':          Object.values(COMISION_TRAMO_ESTADO),
  'brokers.estado':                           Object.values(BROKER_ESTADO),
  'prospectos_documentos.documento_estado':   Object.values(KYC_DOCUMENTO_ESTADO),
  'reglas_descuento.tipo_descuento':          Object.values(DESCUENTO_TIPO),
  'prospectos_interacciones.tipo':            Object.values(INTERACCION_TIPO),
  // M07 — Obra
  'avance_fisico_partida.estatus':            Object.values(AVANCE_ESTATUS),
  'presupuesto_parkinson_detector.trayectoria': Object.values(PARKINSON_TRAYECTORIA),
  'pago_avance_control.estado_control':       Object.values(PAGO_AVANCE_ESTADO),
  'obra_contratos.estatus':                   Object.values(OBRA_CONTRATO_ESTADO),
  'estimaciones_visualizacion.color_gantt':   Object.values(ESTIMACION_VISUALIZACION_COLOR),
  // M08 — Tareas
  'tareas.estado':                            Object.values(TAREA_ESTADO),
  'tareas.prioridad':                         Object.values(TAREA_PRIORIDAD),
  'tareas.vinculo_tipo':                      Object.values(TAREA_VINCULO_TIPO),
  'chats_canales.tipo':                       Object.values(CANAL_TIPO),
  'chats_canales.visibilidad':                Object.values(CANAL_VISIBILIDAD),
  'chats_canal_miembros.rol_canal':           Object.values(CANAL_ROL),
  // M09 — Post-venta
  'tickets_garantia.estatus':                 Object.values(TICKET_ESTADO),
  'tickets_garantia.urgencia':                Object.values(TICKET_PRIORIDAD),
  'tickets_garantia.tipo_defecto':            Object.values(TICKET_CATEGORIA),
  'tickets_garantia.origen':                  Object.values(TICKET_ORIGEN),
  'actas_entrega.estado':                     Object.values(ACTA_ESTADO),
  'escrituraciones.estado':                   Object.values(ESCRITURACION_ESTADO),
  'tickets_diagnostico.estado':               Object.values(DIAGNOSTICO_ESTADO),
  'tickets_asignaciones.estado_asignacion':    Object.values(ASIGNACION_TICKET_ESTADO),
  'tickets_bitacora.tipo_evento':             Object.values(TICKET_BITACORA_TIPO_EVENTO),
  // M10 — Reportes
  'reportes_programados.formato':             Object.values(REPORTE_FORMATO),
  'reportes_programados.frecuencia':          Object.values(REPORTE_FRECUENCIA),
  // M13 — Configuración
  'integraciones_config.resultado_prueba':    Object.values(INTEGRACION_RESULTADO),
  'parametros_globales.tipo':                 Object.values(PARAMETRO_TIPO),
  'parametros_globales.grupo':                Object.values(PARAMETRO_GRUPO),
  // M14 — Overhead
  'reglas_overhead.tipo_regla':               Object.values(OVERHEAD_REGLA_TIPO),
  'aportaciones_overhead.estatus':            Object.values(APORTACION_OVERHEAD_ESTATUS),
  'overhead_gastos.periodicidad':             Object.values(OVERHEAD_GASTO_PERIODICIDAD),
  // M15 — Contabilidad
  'polizas.estatus':                          Object.values(POLIZA_ESTATUS),
};
